import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const {
  LuRocket, LuSettings, LuImage, LuShare2, LuSave, LuRefreshCw, LuCheck,
  LuBookmark, LuCopy, LuCircleAlert, LuSparkles, LuLoader, LuTrash2, LuExternalLink, LuVideo,
} = LuIcons;

// ============ الإعدادات الافتراضية ============
const SETTING_KEYS = {
  api_base: 'gz_api_base',
  usd_rate: 'gz_usd_rate',
  shipping: 'gz_shipping_dzd',
  profit: 'gz_profit_dzd',
  banner_text: 'gz_banner_text',
  whatsapp: 'gz_whatsapp',
  offices: 'gz_offices',
  logo_url: 'gz_logo_url',
  music_url: 'gz_music_url',
  video_secs: 'gz_video_secs',
};

const DEFAULTS = {
  api_base: '',
  usd_rate: '262',
  shipping: '450000',
  profit: '1000000',
  banner_text: 'CarDzair.com',
  whatsapp: '213666667878',
  offices: 'العاصمة: 0661662440\nباتنة: 0770703068\nالصحراء: 0666667878',
  logo_url: 'https://xyzundrsnkrygrekyzle.supabase.co/storage/v1/object/public/images/logo_1776602172593.png',
  music_url: '',
  video_secs: '2.5',
};

// ============ أدوات مساعدة ============
const fmt = (n) => (Number(n) || 0).toLocaleString();

function parsePayloadFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search).get('guazi');
    if (!p) return null;
    const json = decodeURIComponent(escape(atob(p)));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// تحليل كتلة المواصفات الخام القادمة من صفحة Guazi — التحليل هنا وليس في الزر،
// حتى لا يحتاج المستخدم إعادة تثبيت الزر عند أي تحسين مستقبلي
const SPEC_LABELS = [
  'Item No', 'VIN', 'Mfg. Date', '1st Reg. Date', 'Model Year', 'Mileage (km)',
  'Fuel Type', 'Transmission', 'Engine', 'Horsepower (ps)', 'Drive Train',
  'Body Style', 'Seats', 'Doors', 'Exterior Color', 'Dimension (mm)',
  'Curb Weight (kg)', 'Steering',
];

function parseSpecBlock(spec) {
  if (!spec) return {};
  const found = SPEC_LABELS
    .map((label) => ({ label, idx: spec.indexOf(label) }))
    .filter((f) => f.idx >= 0)
    .sort((a, b) => a.idx - b.idx);
  const map = {};
  for (let i = 0; i < found.length; i++) {
    const start = found[i].idx + found[i].label.length;
    const end = i + 1 < found.length ? found[i + 1].idx : spec.length;
    map[found[i].label] = spec.slice(start, end).trim();
  }
  const num = (s) => (s || '').replace(/[^\d.]/g, '');
  const date = (s) => {
    const m = (s || '').match(/(20\d{2})\.(\d{1,2})/);
    return m ? `${m[2].padStart(2, '0')}/${m[1]}` : '';
  };
  return {
    year: num(map['Model Year']).slice(0, 4),
    mfgDate: date(map['Mfg. Date']),
    regDate: date(map['1st Reg. Date']),
    mileageKm: Math.round(Number(num(map['Mileage (km)'])) || 0),
    fuel: map['Fuel Type'] || '',
    transmission: map['Transmission'] || '',
    engine: map['Engine'] || '',
    horsepower: Math.round(Number(num(map['Horsepower (ps)'])) || 0) || '',
    seats: num(map['Seats']),
    body: map['Body Style'] || '',
    color: map['Exterior Color'] || '',
  };
}

function buildBookmarklet(adminOrigin) {
  const code = `(()=>{try{
var id=(location.pathname.match(/-([a-z0-9]{8,14})\\.html/)||[])[1]||'';
var tc=document.body.textContent;
var g=function(re){var m=tc.match(re);return m?m[1]:''};
var title=(document.querySelector('h1')||{}).innerText||document.title;
var d={
 t:title.replace(/^Used\\s+/i,''),
 p:g(/FOB Price:?\\$?\\s?([\\d,]+)/).replace(/,/g,''),
 spec:(tc.match(/Item No[\\s\\S]{0,1500}?Steering(?:Left|Right)/)||[''])[0],
 url:location.href.split('?')[0],
 id:id,
 imgs:[]
};
var seen={};
document.querySelectorAll('img').forEach(function(im){
 var s=im.currentSrc||im.src||'';
 if(!s||s.indexOf('guazistatic')<0)return;
 if(id&&s.indexOf(id)<0)return;
 if(!/\\.(jpe?g|png|webp)/i.test(s))return;
 var base=s.split('?')[0];
 if(seen[base])return;seen[base]=1;
 d.imgs.push(base);
});
d.imgs=d.imgs.slice(0,20);
if(!d.imgs.length){alert('لم أجد صور السيارة — افتح صفحة سيارة من en.guazi.com');return}
var enc=btoa(unescape(encodeURIComponent(JSON.stringify(d))));
window.open('${adminOrigin}/dash-dz-2026?guazi='+encodeURIComponent(enc),'_blank');
}catch(e){alert('خطأ: '+e.message)}})()`;
  return 'javascript:' + encodeURIComponent(code);
}

// كشف شارة Guazi الخضراء: تجميع البكسلات الخضراء في مكوّنات متصلة
// واختيار المكوّن الذي يشبه الشارة (مستطيل عريض كثيف) — يتجاهل لوحات/أجسام خضراء أخرى
function detectGreenBadge(ctx, W, H) {
  const step = 2;
  const data = ctx.getImageData(0, 0, W, H).data;
  const cell = Math.max(8, Math.round(W / 48));
  const gw = Math.ceil(W / cell), gh = Math.ceil(H / cell);
  const grid = new Array(gw * gh).fill(0);
  const boxes = {}; // لكل خلية: إطار بكسلاتها الخضراء

  for (let y = 0; y < H; y += step) {
    for (let x = 0; x < W; x += step) {
      const i = (y * W + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (g > 120 && g > r + 40 && g > b + 40 && r < 140 && b < 140) {
        const ci = Math.floor(y / cell) * gw + Math.floor(x / cell);
        grid[ci]++;
        const bx = boxes[ci] || (boxes[ci] = { minX: x, minY: y, maxX: x, maxY: y });
        if (x < bx.minX) bx.minX = x; if (x > bx.maxX) bx.maxX = x;
        if (y < bx.minY) bx.minY = y; if (y > bx.maxY) bx.maxY = y;
      }
    }
  }

  const cellThreshold = (cell / step) * (cell / step) * 0.18; // خلية "خضراء" فعلاً
  const visited = new Set();
  const components = [];
  for (let ci = 0; ci < grid.length; ci++) {
    if (grid[ci] < cellThreshold || visited.has(ci)) continue;
    // BFS على الخلايا المتجاورة
    const queue = [ci];
    visited.add(ci);
    let count = 0, minX = W, minY = H, maxX = 0, maxY = 0;
    while (queue.length) {
      const c = queue.pop();
      count += grid[c];
      const bx = boxes[c];
      if (bx) {
        if (bx.minX < minX) minX = bx.minX; if (bx.maxX > maxX) maxX = bx.maxX;
        if (bx.minY < minY) minY = bx.minY; if (bx.maxY > maxY) maxY = bx.maxY;
      }
      const cx = c % gw, cy = Math.floor(c / gw);
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = cx + dx, ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
        const n = ny * gw + nx;
        if (!visited.has(n) && grid[n] >= cellThreshold) { visited.add(n); queue.push(n); }
      }
    }
    components.push({ x: minX, y: minY, w: maxX - minX, h: maxY - minY, count });
  }

  // مرشّح شكل اللوحة/الشارة: مستطيل، حجم معقول، كثافة كافية
  // لوحة مقصوصة على حافة الصورة: شروط شكل مخففة
  const candidates = [];
  const smallAnchors = []; // نقاط خضراء صغيرة: قد تكون نقطة شعار Guazi داخل قرص داكن
  for (const c of components) {
    if (c.w > W * 0.55 || c.h > H * 0.3) continue;
    const density = (c.count * step * step) / (Math.max(1, c.w) * Math.max(1, c.h));
    const touchesEdge = c.x <= 4 || c.x + c.w >= W - 4 || c.y + c.h >= H - 4;
    const ratio = c.w / Math.max(1, c.h);
    if (touchesEdge && c.w >= W * 0.02 && c.h >= 8 && density > 0.08) { candidates.push({ ...c, green: true }); continue; }
    if (c.w >= W * 0.025 && c.h >= 8 && ratio >= 0.9 && ratio <= 14 && density > 0.15) { candidates.push({ ...c, green: true }); continue; }
    if (c.w >= 5 && c.w <= W * 0.06 && c.h >= 5 && density > 0.2) smallAnchors.push(c);
  }

  // النوع الداكن: قرص أسود "Guazi.com" بنقطة خضراء — نتوسع من النقطة داخل المنطقة الداكنة
  for (const a of smallAnchors.slice(0, 6)) {
    const cy = Math.round(a.y + a.h / 2);
    const bandH = Math.max(6, a.h);
    const isDarkCol = (x) => {
      let sum = 0, n = 0;
      for (let y = Math.max(0, cy - bandH); y <= Math.min(H - 1, cy + bandH); y += 2) {
        const i = (y * W + x) * 4;
        sum += (data[i] + data[i + 1] + data[i + 2]) / 3; n++;
      }
      return n > 0 && sum / n < 85;
    };
    let L = a.x, R = a.x + a.w;
    while (L > 0 && a.x - L < W * 0.45 && isDarkCol(L - 1)) L--;
    while (R < W - 1 && R - a.x < W * 0.45 && isDarkCol(R + 1)) R++;
    const pw = R - L;
    if (pw >= W * 0.06 && pw <= W * 0.45 && pw / (bandH * 2 + a.h) > 1.6) {
      // ارتفاع القرص: توسّع عمودي حول المركز
      const midX = Math.round((L + R) / 2);
      const isDarkRow = (y) => {
        let sum = 0, n = 0;
        for (let x = Math.max(0, midX - Math.round(pw * 0.3)); x <= Math.min(W - 1, midX + Math.round(pw * 0.3)); x += 3) {
          const i = (y * W + x) * 4;
          const bright = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (bright < 110) sum++; n++;
        }
        return n > 0 && sum / n > 0.55;
      };
      let T = cy, B = cy;
      while (T > 0 && cy - T < H * 0.12 && isDarkRow(T - 1)) T--;
      while (B < H - 1 && B - cy < H * 0.12 && isDarkRow(B + 1)) B++;
      const ph = B - T;
      if (ph >= 12 && ph <= H * 0.2 && pw / ph >= 1.8 && pw / ph <= 14) {
        // لا نضيف قرصاً يتقاطع مع لوحة خضراء مكتشفة أصلاً (نفس العلامة)
        const overlaps = candidates.some((c) => L < c.x + c.w && L + pw > c.x && T < c.y + c.h && T + ph > c.y);
        if (!overlaps && pw * ph <= W * H * 0.12) {
          candidates.push({ x: L, y: T, w: pw, h: ph, count: 0, green: false });
        }
      }
    }
  }

  // تحليل هندسي للّوحات الخضراء: نقيس من شكل البقعة الحجمَ الحقيقي والدوران والمنظور
  const isGreenPx = (px, py) => {
    const i = (py * W + px) * 4;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    return g > 120 && g > r + 40 && g > b + 40 && r < 140 && b < 140;
  };
  for (const c of candidates) {
    if (!c.green || c.w < 24 || c.h < 10) continue;
    const x0 = Math.max(0, c.x), x1 = Math.min(W - 1, c.x + c.w);
    const y0 = Math.max(0, c.y), y1 = Math.min(H - 1, c.y + c.h);
    const colH = [], colC = [], colX = [];
    const rowW = [];
    for (let px = x0; px <= x1; px++) {
      let cnt = 0, mn = 1e9, mx = -1;
      for (let py = y0; py <= y1; py++) if (isGreenPx(px, py)) { cnt++; if (py < mn) mn = py; if (py > mx) mx = py; }
      if (cnt >= 3) { colH.push(mx - mn + 1); colC.push((mn + mx) / 2); colX.push(px); }
    }
    for (let py = y0; py <= y1; py++) {
      let cnt = 0;
      for (let px = x0; px <= x1; px++) if (isGreenPx(px, py)) cnt++;
      if (cnt >= 3) rowW.push(cnt);
    }
    if (colX.length < 10 || rowW.length < 6) continue;

    const med = (arr) => { const a = [...arr].sort((p, q) => p - q); return a[Math.floor(a.length / 2)]; };
    const avg = (arr) => arr.reduce((p, q) => p + q, 0) / arr.length;

    // الدوران: انحدار خط مراكز الأعمدة
    const mx2 = avg(colX), my2 = avg(colC);
    let num = 0, den = 0;
    for (let i2 = 0; i2 < colX.length; i2++) { num += (colX[i2] - mx2) * (colC[i2] - my2); den += (colX[i2] - mx2) ** 2; }
    const slope = den ? num / den : 0;
    let rot = (Math.atan(slope) * 180) / Math.PI;
    rot = Math.max(-25, Math.min(25, Math.round(rot)));

    // المنظور الأفقي: نسبة ارتفاع الطرفين (الأبعد أقصر)
    const q = Math.max(3, Math.floor(colH.length / 4));
    const hL = avg(colH.slice(0, q)), hR = avg(colH.slice(-q));
    let ry = 0;
    if (hL > 4 && hR > 4) {
      const ratio = hR / hL;
      const sinT = Math.max(-0.75, Math.min(0.75, (4.4 * (1 - ratio)) / (1 + ratio)));
      ry = Math.round((Math.asin(sinT) * 180) / Math.PI);
      if (Math.abs(ry) < 10) ry = 0; // ضجيج توزيع النص داخل اللافتة
    }
    // المنظور العمودي: نسبة عرض الأعلى للأسفل
    const q2 = Math.max(2, Math.floor(rowW.length / 4));
    const wT = avg(rowW.slice(0, q2)), wB = avg(rowW.slice(-q2));
    let rx = 0;
    if (wT > 6 && wB > 6) {
      const ratio2 = wB / wT;
      const sinT2 = Math.max(-0.6, Math.min(0.6, (4.4 * (1 - ratio2)) / (1 + ratio2)));
      rx = Math.round((Math.asin(sinT2) * 180) / Math.PI);
      if (Math.abs(rx) < 18) rx = 0; // الصفوف أكثر عرضة للضجيج (نص وزوايا مدورة)
    }

    // الحجم الحقيقي: وسيط عرض الصفوف وارتفاع الأعمدة (أدق من صندوق الإحاطة مع الميلان)
    c.quad = {
      w: Math.round(med(rowW)),
      h: Math.round(med(colH)),
      cx: mx2,
      cy: my2,
      rot, ry: Math.max(-50, Math.min(50, ry)), rx: Math.max(-35, Math.min(35, rx)),
    };
  }

  // الأخضر أولاً (أعلى ثقة)، ثم الأكبر مساحة — بمقياس واحد لكل الأنواع
  candidates.sort((a, b) => (b.green ? 1 : 0) - (a.green ? 1 : 0) || (b.w * b.h) - (a.w * a.h));
  return candidates;
}

// تحميل شعار الموقع مرة واحدة مع اقتصاص الحواف الفارغة (شفافة أو بيضاء)
let _logoCache = { url: null, canvas: null };
async function loadTrimmedLogo(logoUrl) {
  if (!logoUrl) return null;
  if (_logoCache.url === logoUrl) return _logoCache.canvas;
  try {
    const resp = await fetch(logoUrl, { mode: 'cors' });
    const bmp = await createImageBitmap(await resp.blob());
    const c = document.createElement('canvas');
    c.width = bmp.width; c.height = bmp.height;
    const x = c.getContext('2d');
    x.drawImage(bmp, 0, 0);
    const d = x.getImageData(0, 0, c.width, c.height).data;
    let minX = c.width, minY = c.height, maxX = 0, maxY = 0;
    for (let y = 0; y < c.height; y += 2) {
      for (let xx = 0; xx < c.width; xx += 2) {
        const i = (y * c.width + xx) * 4;
        const opaque = d[i + 3] > 40;
        const nonWhite = d[i] < 235 || d[i + 1] < 235 || d[i + 2] < 235;
        if (opaque && nonWhite) {
          if (xx < minX) minX = xx; if (xx > maxX) maxX = xx;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX <= minX || maxY <= minY) { _logoCache = { url: logoUrl, canvas: c }; return c; }
    const tc = document.createElement('canvas');
    tc.width = maxX - minX; tc.height = maxY - minY;
    tc.getContext('2d').drawImage(c, minX, minY, tc.width, tc.height, 0, 0, tc.width, tc.height);
    _logoCache = { url: logoUrl, canvas: tc };
    return tc;
  } catch {
    return null;
  }
}

// الشكل الافتراضي للافتة (يحفظه المستخدم بزر "اجعله الافتراضي") — يُطبق على الاكتشاف التلقائي والنقاط الجديدة
const STAMP_DEFAULTS = { style: 'plate', corner: 44, margin: 14, border: 50, shadow: 50 };
function loadStampDefaults() {
  try { return { ...STAMP_DEFAULTS, ...JSON.parse(localStorage.getItem('gz_stamp_defaults') || '{}') }; }
  catch { return { ...STAMP_DEFAULTS }; }
}
function saveStampDefaults(d) {
  localStorage.setItem('gz_stamp_defaults', JSON.stringify({
    style: d.style || 'plate',
    corner: d.corner !== undefined ? d.corner : 44,
    margin: d.margin !== undefined ? d.margin : 14,
    border: d.border !== undefined ? d.border : 50,
    shadow: d.shadow !== undefined ? d.shadow : 50,
  }));
}

// حفظ دائم لتعديلات كل صورة (مفتاحها رابط الصورة) — حتى لا تضيع التعديلات عند تحديث الصفحة
function loadSpotsStore() {
  try { return JSON.parse(localStorage.getItem('gz_spots_v1') || '{}'); } catch { return {}; }
}
function persistSpots(src, spots) {
  try {
    const store = loadSpotsStore();
    store[src] = spots;
    const keys = Object.keys(store);
    if (keys.length > 300) keys.slice(0, keys.length - 300).forEach((k) => delete store[k]);
    localStorage.setItem('gz_spots_v1', JSON.stringify(store));
  } catch { /* امتلاء التخزين لا يوقف العمل */ }
}

// نسخة شفافة من الشعار: الأبيض يصبح شفافاً (للختم مباشرة على جسم السيارة بلا خلفية)
let _transCache = { src: null, canvas: null };
function makeTransparentLogo(logoCanvas) {
  if (!logoCanvas) return null;
  if (_transCache.src === logoCanvas) return _transCache.canvas;
  const c = document.createElement('canvas');
  c.width = logoCanvas.width; c.height = logoCanvas.height;
  const x = c.getContext('2d');
  x.drawImage(logoCanvas, 0, 0);
  const id = x.getImageData(0, 0, c.width, c.height);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    const dist = 255 - Math.min(d[i], d[i + 1], d[i + 2]); // البعد عن الأبيض
    const a = dist < 40 ? 0 : dist > 120 ? 1 : (dist - 40) / 80;
    if (a === 0) { d[i + 3] = 0; continue; }
    if (a < 1) {
      // فكّ خلط الأبيض: نستعيد اللون الأصلي للحافة حتى لا تظهر هالة/خط أبيض حول اللوجو
      d[i]     = Math.min(255, Math.max(0, Math.round((d[i]     - 255 * (1 - a)) / a)));
      d[i + 1] = Math.min(255, Math.max(0, Math.round((d[i + 1] - 255 * (1 - a)) / a)));
      d[i + 2] = Math.min(255, Math.max(0, Math.round((d[i + 2] - 255 * (1 - a)) / a)));
    }
    d[i + 3] = Math.round(a * d[i + 3]);
  }
  x.putImageData(id, 0, 0);
  _transCache = { src: logoCanvas, canvas: c };
  return c;
}

// برغي معدني بمجرى صليبي — لزوايا اللافتة البيضاء
function drawPlateScrew(x, cx, cy, r) {
  x.save();
  x.shadowColor = 'rgba(0,0,0,0.45)';
  x.shadowBlur = r * 0.5;
  x.shadowOffsetY = r * 0.15;
  const g = x.createRadialGradient(cx - r * 0.35, cy - r * 0.35, r * 0.1, cx, cy, r);
  g.addColorStop(0, '#f2f3f5');
  g.addColorStop(0.55, '#b9bcc2');
  g.addColorStop(1, '#7d8087');
  x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2);
  x.fillStyle = g; x.fill();
  x.restore();
  x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2);
  x.strokeStyle = 'rgba(60,60,68,0.8)';
  x.lineWidth = Math.max(1, r * 0.12);
  x.stroke();
  x.save();
  x.translate(cx, cy); x.rotate(0.44);
  x.strokeStyle = 'rgba(55,55,62,0.9)';
  x.lineWidth = Math.max(1, r * 0.22);
  x.lineCap = 'round';
  x.beginPath();
  x.moveTo(-r * 0.6, 0); x.lineTo(r * 0.6, 0);
  x.moveTo(0, -r * 0.6); x.lineTo(0, r * 0.6);
  x.stroke();
  x.restore();
}

// منظور ثلاثي الأبعاد حقيقي — نفس معادلات CSS في معاينة المحرر حرفياً:
// perspective(d) rotateY(ry) rotateX(-rx) حيث d = 2.2 × أكبر بُعد للافتة
// يُرسم بشبكة مثلثات صغيرة فيطابق الناتجُ النهائي المعاينةَ تماماً
function projectSprite(src, ryDeg, rxDeg, plateW, plateH) {
  const w = src.width, h = src.height;
  const d = 2.2 * Math.max(plateW, plateH);
  const tx = (-(rxDeg || 0) * Math.PI) / 180; // المعاينة تعكس إشارة المنظور العمودي
  const ty = ((ryDeg || 0) * Math.PI) / 180;
  const cX = Math.cos(tx), sX = Math.sin(tx);
  const cY = Math.cos(ty), sY = Math.sin(ty);

  // نقطة (x,y) بإحداثيات ممركزة → إسقاطها كما يفعل CSS بالضبط
  const map = (x, y) => {
    const y1 = y * cX, z1 = y * sX;          // rotateX أولاً (الأيمن في سلسلة CSS يُطبق أولاً)
    const x2 = x * cY + z1 * sY;             // ثم rotateY
    const z2 = -x * sY + z1 * cY;
    let s = d / (d - z2);                    // ثم الإسقاط المنظوري
    if (!isFinite(s) || s < 0.15) s = 0.15;
    if (s > 5) s = 5;
    return [x2 * s, y1 * s];
  };

  // حدود الناتج (متناظرة حول المركز حتى يبقى مركز اللافتة ثابتاً)
  let mx = 1, my = 1;
  const corners = [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]];
  for (const [px, py] of corners) {
    const [qx, qy] = map(px, py);
    mx = Math.max(mx, Math.abs(qx)); my = Math.max(my, Math.abs(qy));
  }
  const out = document.createElement('canvas');
  out.width = Math.ceil(mx * 2) + 4; out.height = Math.ceil(my * 2) + 4;
  const g = out.getContext('2d');
  g.translate(out.width / 2, out.height / 2);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';

  // مثلث واحد: تحويل أفيني من ثلاث نقاط مصدر إلى ثلاث نقاط وجهة
  const tri = (s0, s1, s2, d0, d1, d2) => {
    const den = s0[0] * (s1[1] - s2[1]) + s1[0] * (s2[1] - s0[1]) + s2[0] * (s0[1] - s1[1]);
    if (!den) return;
    const A = (d0[0] * (s1[1] - s2[1]) + d1[0] * (s2[1] - s0[1]) + d2[0] * (s0[1] - s1[1])) / den;
    const B = (d0[1] * (s1[1] - s2[1]) + d1[1] * (s2[1] - s0[1]) + d2[1] * (s0[1] - s1[1])) / den;
    const C = (d0[0] * (s2[0] - s1[0]) + d1[0] * (s0[0] - s2[0]) + d2[0] * (s1[0] - s0[0])) / den;
    const D = (d0[1] * (s2[0] - s1[0]) + d1[1] * (s0[0] - s2[0]) + d2[1] * (s1[0] - s0[0])) / den;
    const E = (d0[0] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[0] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[0] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
    const F = (d0[1] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[1] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[1] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
    // توسيع المثلث قليلاً حول مركزه لإخفاء خطوط التماس بين المثلثات
    const gx = (d0[0] + d1[0] + d2[0]) / 3, gy = (d0[1] + d1[1] + d2[1]) / 3, X = 1.04;
    g.save();
    g.beginPath();
    g.moveTo(gx + (d0[0] - gx) * X, gy + (d0[1] - gy) * X);
    g.lineTo(gx + (d1[0] - gx) * X, gy + (d1[1] - gy) * X);
    g.lineTo(gx + (d2[0] - gx) * X, gy + (d2[1] - gy) * X);
    g.closePath();
    g.clip();
    g.transform(A, B, C, D, E, F);
    g.drawImage(src, -w / 2, -h / 2);
    g.restore();
  };

  const N = 24; // دقة الشبكة
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const x0 = -w / 2 + (i * w) / N,     y0 = -h / 2 + (j * h) / N;
      const x1 = -w / 2 + ((i + 1) * w) / N, y1 = -h / 2 + ((j + 1) * h) / N;
      const s00 = [x0, y0], s10 = [x1, y0], s11 = [x1, y1], s01 = [x0, y1];
      const p00 = map(x0, y0), p10 = map(x1, y0), p11 = map(x1, y1), p01 = map(x0, y1);
      tri(s00, s10, s11, p00, p10, p11);
      tri(s00, s11, s01, p00, p11, p01);
    }
  }
  return out;
}

// رسم بطاقة الشعار في لوحة مستقلة (مع هامش يستوعب تمدد المنظور والظلال) ثم تشويهها
// style: 'plate' لافتة بيضاء مجسمة ببراغي | 'trans' شعار شفاف مباشرة على السيارة
// corner: استدارة الحواف 0..100 (0 زوايا حادة، 100 شبه دائرية) — الافتراضي 44 كالسابق
// margin: هامش أبيض حول اللوجو % من اللافتة (0 = اللوجو يملأها كلافتة حقيقية)
// border: سماكة خط الإطار المحيط باللافتة 0..100 (0 = بلا خط نهائياً، 50 = السماكة القديمة)
// shadow: شدة الظلال 0..100 (0 = لا ظل إطلاقاً — لا شيء يحيط باللافتة)
function buildStampSprite({ w, h, logo, bannerText, style = 'plate', corner = 44, margin = 14, border = 50, shadow = 50 }) {
  const shf = Math.min(100, Math.max(0, shadow)) / 50; // 1 = الشدة القديمة
  const mx = Math.round(w * 0.35), my = Math.round(h * 0.5);
  const c = document.createElement('canvas');
  c.width = w + mx * 2; c.height = h + my * 2;
  const x = c.getContext('2d');
  x.translate(c.width / 2, c.height / 2);

  // ── شعار شفاف: بلا خلفية، مع ظل عمق يجعله كأنه ملصق على الطلاء ──
  if (logo && style === 'trans') {
    const trans = makeTransparentLogo(logo) || logo;
    const sc = Math.min(w / trans.width, h / trans.height);
    const dw = trans.width * sc, dh = trans.height * sc;
    x.save();
    if (shf > 0) {
      x.shadowColor = `rgba(0,0,0,${0.55 * Math.min(1.6, shf)})`;
      x.shadowBlur = Math.max(2, dh * 0.06) * shf;
      x.shadowOffsetX = Math.max(1, dw * 0.008) * shf;
      x.shadowOffsetY = Math.max(2, dh * 0.025) * shf;
    }
    x.drawImage(trans, -dw / 2, -dh / 2, dw, dh);
    x.restore();
    return c;
  }

  const r = Math.round(h * 0.5 * (Math.min(100, Math.max(0, corner)) / 100));
  const rr = (pad = 0, rad = r) => {
    x.beginPath();
    if (x.roundRect) x.roundRect(-w / 2 + pad, -h / 2 + pad, w - pad * 2, h - pad * 2, rad);
    else x.rect(-w / 2 + pad, -h / 2 + pad, w - pad * 2, h - pad * 2);
  };

  if (!logo) {
    // تغطية داكنة بنص احتياطي (كما كانت)
    rr();
    x.fillStyle = '#0f172a';
    x.fill();
    x.fillStyle = '#ffffff';
    let fontPx = Math.round(h * 0.42);
    x.font = `bold ${fontPx}px Arial, sans-serif`;
    while (x.measureText(bannerText).width > w * 0.88 && fontPx > 9) {
      fontPx -= 1;
      x.font = `bold ${fontPx}px Arial, sans-serif`;
    }
    x.textAlign = 'center'; x.textBaseline = 'middle';
    if (fontPx > 9) x.fillText(bannerText, 0, 1);
    return c;
  }

  // ── لافتة بيضاء مجسمة: ظل ساقط + تدرج + حواف بارزة + لمعان + براغي ──
  x.save();
  if (shf > 0) {
    x.shadowColor = `rgba(0,0,0,${0.5 * Math.min(1.6, shf)})`;
    x.shadowBlur = h * 0.22 * shf;
    x.shadowOffsetY = h * 0.08 * shf;
  }
  rr();
  x.fillStyle = '#ffffff';
  x.fill();
  x.restore();

  const grad = x.createLinearGradient(0, -h / 2, 0, h / 2);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(1, '#e6e7ea');
  rr();
  x.fillStyle = grad;
  x.fill();

  // خط الإطار: سماكته من المنزلق، وعند 0 لا يُرسم أصلاً (مظهر أنظف وأكثر واقعية)
  const bw = Math.min(100, Math.max(0, border)) / 50; // 1 = السماكة القديمة
  if (bw > 0) {
    rr();
    x.strokeStyle = 'rgba(120,120,128,0.55)';
    x.lineWidth = Math.max(0.5, h * 0.012 * bw);
    x.stroke();
    rr(h * 0.02, r * 0.9);
    x.strokeStyle = 'rgba(255,255,255,0.85)';
    x.lineWidth = Math.max(0.5, h * 0.015 * bw);
    x.stroke();
  }

  x.save();
  rr();
  x.clip();
  const sheen = x.createLinearGradient(-w / 2, -h / 2, w * 0.1, h / 2);
  sheen.addColorStop(0, 'rgba(255,255,255,0.35)');
  sheen.addColorStop(0.45, 'rgba(255,255,255,0)');
  x.fillStyle = sheen;
  x.fillRect(-w / 2, -h / 2, w, h);
  x.restore();

  const trans = makeTransparentLogo(logo) || logo;
  const m = Math.min(1, Math.max(0.3, 1 - (Math.min(35, Math.max(0, margin)) * 2) / 100));
  const sc = Math.min((w * m) / trans.width, (h * m) / trans.height);
  const dw = trans.width * sc, dh = trans.height * sc;
  x.save();
  if (shf > 0) {
    x.shadowColor = `rgba(30,30,60,${0.35 * Math.min(1.6, shf)})`;
    x.shadowBlur = Math.max(1, dh * 0.04) * shf;
    x.shadowOffsetY = Math.max(1, dh * 0.02) * shf;
  }
  x.drawImage(trans, -dw / 2, -dh / 2, dw, dh);
  x.restore();

  // البراغي فوق كل شيء — كأنها تثبّت اللافتة فعلاً حتى لو ملأها اللوجو
  const sr = Math.min(w, h) * 0.055;
  const inset = Math.min(w, h) * 0.14;
  drawPlateScrew(x, -w / 2 + inset, -h / 2 + inset, sr);
  drawPlateScrew(x, w / 2 - inset, -h / 2 + inset, sr);
  drawPlateScrew(x, -w / 2 + inset, h / 2 - inset, sr);
  drawPlateScrew(x, w / 2 - inset, h / 2 - inset, sr);
  return c;
}

// معالجة صورة: استبدال لوحة Guazi الخضراء بشعار CarDzair بنفس المكان والحجم
// manualSpots: نقاط تصحيح يدوية (نِسَب 0..1) يضغطها المستخدم فوق لافتة لم تُكتشف
async function processImage(proxiedUrl, bannerText, logoUrl, manualSpots = [], autoDetect = true) {
  const resp = await fetch(proxiedUrl);
  if (!resp.ok) throw new Error('فشل جلب الصورة عبر البروكسي');
  const blob = await resp.blob();
  const bmp = await createImageBitmap(blob);
  const W = Math.min(bmp.width, 1920); // دقة أعلى حتى تبقى اللافتة حادة في الموقع والفيديو
  const scale = W / bmp.width;
  const H = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0, W, H);
  const found = autoDetect ? detectGreenBadge(ctx, W, H) : [];

  // نموذج موحّد لكل بصمة شعار: مركز + أبعاد + دوران — يجمع الاكتشاف التلقائي والتصحيح اليدوي
  const DEF = loadStampDefaults(); // الشكل المفضل المحفوظ يُطبق على الاكتشاف التلقائي أيضاً
  const stamps = [];
  found.forEach((b, i) => {
    const isMain = i === 0 && b.w > W * 0.06;
    if (b.quad && isMain) {
      // القياس الهندسي: حجم حقيقي + دوران + منظور مأخوذة من شكل اللافتة نفسها
      const pad = Math.max(3, Math.round(b.quad.h * 0.16));
      // لوحة مقصوصة على حافة الصورة تعطي قياسات منظور مجنونة — نقيّدها ونلغي المنظور عند الحواف
      const clampDeg = (v, lim) => Math.max(-lim, Math.min(lim, v || 0));
      const touchesEdge = b.quad.cx - b.quad.w / 2 < 4 || b.quad.cy - b.quad.h / 2 < 4
        || b.quad.cx + b.quad.w / 2 > W - 4 || b.quad.cy + b.quad.h / 2 > H - 4;
      stamps.push({
        cx: b.quad.cx, cy: b.quad.cy,
        w: b.quad.w + pad * 2, h: b.quad.h + pad * 2,
        rot: clampDeg(b.quad.rot, 25),
        ry: touchesEdge ? 0 : clampDeg(b.quad.ry, 35),
        rx: touchesEdge ? 0 : clampDeg(b.quad.rx, 35),
        logo: true, ...DEF, style: 'plate', // النمط الشفاف لا يخفي اللوحة الخضراء — التلقائي دائماً لافتة بيضاء
      });
      return;
    }
    const pad = Math.max(3, Math.round(b.h * 0.14));
    const w = b.w + pad * 2, h = b.h + pad * 2;
    stamps.push({
      cx: b.x - pad + w / 2,
      cy: b.y - pad + h / 2,
      w, h, rot: 0,
      logo: isMain, ...DEF, style: 'plate',
    });
  });
  manualSpots.forEach((s) => {
    const w = Math.round(W * (s.scale || 0.22));
    stamps.push({
      cx: s.fx * W,
      cy: s.fy * H,
      w,
      h: Math.round(w * (s.ratio || 0.3)),
      rot: s.rot || 0,
      ry: s.ry || 0,
      rx: s.rx || 0,
      logo: s.logo !== false,
      style: s.style || DEF.style,
      corner: s.corner !== undefined ? s.corner : DEF.corner,
      margin: s.margin !== undefined ? s.margin : DEF.margin,
      border: s.border !== undefined ? s.border : DEF.border,
      shadow: s.shadow !== undefined ? s.shadow : DEF.shadow,
    });
  });

  const toBlob = () => new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.93));
  const boxesOf = () => stamps.map((st) => ({
    fx: st.cx / W, fy: st.cy / H, scale: st.w / W, ratio: st.h / st.w, rot: st.rot, ry: st.ry || 0, rx: st.rx || 0, logo: st.logo, style: st.style || 'plate',
    corner: st.corner !== undefined ? st.corner : 44, margin: st.margin !== undefined ? st.margin : 14, border: st.border !== undefined ? st.border : 50, shadow: st.shadow !== undefined ? st.shadow : 50,
  }));

  if (!stamps.length) {
    // لا توجد لوحة/شارة Guazi في هذه الصورة — نتركها نظيفة كما هي
    return { blob: await toBlob(), detected: 0, boxes: [] };
  }

  const logo = await loadTrimmedLogo(logoUrl);

  for (const st of stamps) {
    const useLogo = st.logo && logo;
    // مسار موحد: نرسم البطاقة (لافتة بيضاء مجسمة / شعار شفاف / تغطية) كصورة مستقلة ثم نسقطها
    let sprite = buildStampSprite({ w: st.w, h: st.h, logo: useLogo ? logo : null, bannerText, style: st.style || 'plate', corner: st.corner, margin: st.margin, border: st.border, shadow: st.shadow });
    if (st.ry || st.rx) sprite = projectSprite(sprite, st.ry || 0, st.rx || 0, st.w, st.h);
    ctx.save();
    ctx.translate(st.cx, st.cy);
    if (st.rot) ctx.rotate((st.rot * Math.PI) / 180);
    ctx.drawImage(sprite, -sprite.width / 2, -sprite.height / 2);
    ctx.restore();
  }

  return { blob: await toBlob(), detected: found.length, boxes: boxesOf() };
}

// مكتبة الأصوات: سطر لكل مقطع بصيغة "الاسم|الرابط"
function parseTracks(raw) {
  return (raw || '')
    .split(String.fromCharCode(10))
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const i = l.indexOf('|');
      if (i === -1) {
        const base = decodeURIComponent((l.split('/').pop() || '').split('?')[0]);
        const noStamp = base.split(String.fromCharCode(95)).filter((p, i) => !(i === 0 && /^[0-9]{8,}$/.test(p)));
        const joined = noStamp.join(' ');
        const noExt = joined.includes('.') ? joined.slice(0, joined.lastIndexOf('.')) : joined;
        const nice = noExt.split('-').join(' ').split(/\s+/).filter(Boolean).join(' ');
        return { name: (nice || 'مقطع').slice(0, 38), url: l };
      }
      return { name: l.slice(0, i).trim(), url: l.slice(i + 1).trim() };
    })
    .filter((t) => /^https?:/.test(t.url));
}

// بطاقة الغلاف للفيديو — العربية تُرسم بالمتصفح فلا مشاكل خطوط على الخادم
async function buildTitleCard({ title, priceLine, badges, offices, logoUrl, landscape = false }) {
  const W = landscape ? 1920 : 1080, H = landscape ? 1080 : 1920;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');

  const grad = x.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#0F172A');
  grad.addColorStop(1, '#134E4A');
  x.fillStyle = grad;
  x.fillRect(0, 0, W, H);

  // الشعار في الأعلى على بطاقة بيضاء
  const logo = await loadTrimmedLogo(logoUrl);
  if (logo) {
    const bw = W * 0.72, bh = bw * 0.3;
    const bx = (W - bw) / 2, by = H * 0.1;
    x.beginPath();
    if (x.roundRect) x.roundRect(bx, by, bw, bh, bh * 0.18); else x.rect(bx, by, bw, bh);
    x.fillStyle = '#ffffff';
    x.fill();
    const s = Math.min((bw * 0.88) / logo.width, (bh * 0.88) / logo.height);
    x.drawImage(logo, bx + (bw - logo.width * s) / 2, by + (bh - logo.height * s) / 2, logo.width * s, logo.height * s);
  }

  x.textAlign = 'center';
  x.direction = 'rtl';

  // اسم السيارة (سطران إن طال)
  x.fillStyle = '#ffffff';
  const words = title.split(' ');
  const mid = Math.ceil(words.length / 2);
  const lines = words.length > 3 ? [words.slice(0, mid).join(' '), words.slice(mid).join(' ')] : [title];
  let fs = 96;
  x.font = `bold ${fs}px Arial, sans-serif`;
  while (lines.some((l) => x.measureText(l).width > W * 0.88) && fs > 40) {
    fs -= 4;
    x.font = `bold ${fs}px Arial, sans-serif`;
  }
  lines.forEach((l, i) => x.fillText(l, W / 2, H * 0.33 + i * fs * 1.2));

  // شرائح المواصفات
  let by2 = H * 0.5;
  x.font = `bold 52px Arial, sans-serif`;
  for (const b of badges.filter(Boolean).slice(0, 4)) {
    const tw = x.measureText(b).width;
    const pw = tw + 70, ph = 84;
    x.beginPath();
    if (x.roundRect) x.roundRect((W - pw) / 2, by2 - ph / 2, pw, ph, ph / 2); else x.rect((W - pw) / 2, by2 - ph / 2, pw, ph);
    x.fillStyle = 'rgba(255,255,255,0.12)';
    x.fill();
    x.fillStyle = '#ffffff';
    x.textBaseline = 'middle';
    x.fillText(b, W / 2, by2);
    by2 += ph + 26;
  }

  // السعر
  x.textBaseline = 'alphabetic';
  x.fillStyle = '#34D399';
  x.font = `bold 74px Arial, sans-serif`;
  let pfs = 74;
  while (x.measureText(priceLine).width > W * 0.9 && pfs > 34) {
    pfs -= 3;
    x.font = `bold ${pfs}px Arial, sans-serif`;
  }
  x.fillText(priceLine, W / 2, H * 0.79);

  // أرقام المكاتب
  x.fillStyle = 'rgba(255,255,255,0.9)';
  x.font = `bold 44px Arial, sans-serif`;
  offices.slice(0, 3).forEach((o, i) => x.fillText('📞 ' + o, W / 2, H * 0.86 + i * 58));

  return new Promise((r) => c.toBlob((b) => r(b), 'image/jpeg', 0.9));
}

// شريط تشويق فوق أول صورة عندما تكون بطاقة السعر في نهاية الفيديو
// يبقي المشاهد حتى النهاية: "السعر والمواصفات في نهاية الفيديو"
async function addTeaserBanner(blob, text) {
  const bmp = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bmp.width; c.height = bmp.height;
  const x = c.getContext('2d');
  x.drawImage(bmp, 0, 0);
  const W = c.width, H = c.height;

  let fontPx = Math.round(Math.min(W, H) * 0.055);
  x.font = `900 ${fontPx}px Arial, sans-serif`;
  while (x.measureText(text).width > W * 0.86 && fontPx > 14) {
    fontPx -= 2;
    x.font = `900 ${fontPx}px Arial, sans-serif`;
  }
  const tw = x.measureText(text).width;
  const padX = fontPx * 0.9, padY = fontPx * 0.5;
  const bw = tw + padX * 2, bh = fontPx + padY * 2;
  const bx = (W - bw) / 2, by = H * 0.045;

  x.save();
  x.shadowColor = 'rgba(0,0,0,0.4)';
  x.shadowBlur = bh * 0.25;
  x.shadowOffsetY = bh * 0.08;
  x.beginPath();
  if (x.roundRect) x.roundRect(bx, by, bw, bh, bh / 2); else x.rect(bx, by, bw, bh);
  x.fillStyle = 'rgba(15,23,42,0.85)';
  x.fill();
  x.restore();
  x.beginPath();
  if (x.roundRect) x.roundRect(bx, by, bw, bh, bh / 2); else x.rect(bx, by, bw, bh);
  x.strokeStyle = 'rgba(251,191,36,0.9)';
  x.lineWidth = Math.max(1.5, fontPx * 0.06);
  x.stroke();

  x.fillStyle = '#fbbf24';
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  x.direction = 'rtl';
  x.fillText(text, W / 2, by + bh / 2 + fontPx * 0.06);

  return new Promise((r) => c.toBlob((b) => r(b), 'image/jpeg', 0.9));
}

// ============ توليد منشورات كل منصة ============
function buildCaptions({ car, totals, settings, link }) {
  const title = `${car.brand} ${car.model} ${car.year}`.trim();
  const isNew = Number(car.mileage) <= 100;
  const newTag = isNew ? '(جديدة 0 كم)' : `(مستعملة — ${fmt(car.mileage)} كم)`;
  const portM = Math.round((totals.priceDzd + totals.shipping) / 10000); // مليون سنتيم
  const totalM = totals.customs > 0 ? Math.round(totals.total / 10000) : 0;
  // توضيح إجباري حتى لا يظن الزبون أن السعر شامل الجمركة وهي غير محسوبة
  const portNote = totalM
    ? '(شامل ثمن السيارة + الشحن البحري)'
    : '(ثمن السيارة + الشحن البحري فقط — غير شامل الجمركة ⚠️)';

  // وضع السعر المباشر: تكتب السعر النهائي بنفسك (نسخ إعلان من مصدر آخر)
  const directM = totals.direct ? Math.round(totals.total / 10000) : 0;
  const custTag = totals.includesCustoms ? 'شامل الجمركة ✅' : '⚠️ غير شامل الجمركة';
  const autoFb = `* 🇩🇿 السعر واصلة للميناء في الجزائر: ${portM} مليون سنتيم ${portNote}.${totalM ? `\n* 🛃 السعر الإجمالي شامل الجمركة: ${totalM} مليون سنتيم.` : '\n* 🛃 الجمركة غير محسوبة — تُضاف حسب نوع السيارة وعمرها.'}`;
  const autoIg = `• واصلة للميناء (سيارة + شحن): ${portM} مليون سنتيم${totalM ? `\n• شاملة الجمركة: ${totalM} مليون سنتيم` : '\n• ⚠️ غير شاملة الجمركة'}`;
  const autoTt = `💵 ${portM} مليون سنتيم واصلة للميناء (ثمن + شحن${totalM ? '' : ' — بدون جمركة'}) 🚢🇩🇿`;
  const autoTg = `• واصلة للميناء: ${portM} مليون سنتيم ${portNote}.${totalM ? `\n• شاملة الجمركة: ${totalM} مليون سنتيم.` : '\n• ⚠️ الجمركة غير محسوبة — تُضاف حسب نوع السيارة وعمرها.'}`;

  const priceFb = totals.direct ? `* 💵 السعر: ${fmt(directM)} مليون سنتيم ${custTag}.` : autoFb;
  const priceIg = totals.direct ? `• 💵 ${fmt(directM)} مليون سنتيم ${custTag}` : autoIg;
  const priceTt = totals.direct ? `💵 ${fmt(directM)} مليون سنتيم ${custTag} 🇩🇿` : autoTt;
  const priceTg = totals.direct ? `• السعر: ${fmt(directM)} مليون سنتيم ${custTag}.` : autoTg;
  const trans = car.transmission === 'automatic' ? 'أوتوماتيكي' : 'يدوي';
  const tag = `${car.brand}${car.model}`.replace(/[\s-]+/g, '');
  const offices = (settings.offices || '').split('\n').filter(Boolean);
  const officesFb = offices.map((o) => `📍 مكتب ${o.trim()}`).join('\n');
  const officesDot = offices.map((o) => `• ${o.trim()}`).join('\n');

  const specLines = [];
  if (car.regDate) {
    // حساب عمر السيارة من أول تسجيل — قاعدة الاستيراد الجزائرية: أقل من 3 سنوات
    const [mm, yy] = car.regDate.split('/').map(Number);
    let ageNote = '';
    if (yy && mm) {
      const now = new Date();
      const ageMonths = (now.getFullYear() - yy) * 12 + (now.getMonth() + 1 - mm);
      ageNote = ageMonths < 36 ? ' ✅ أقل من 3 سنوات — قابلة للاستيراد' : '';
    }
    specLines.push(`🗓️ أول تسجيل: ${car.regDate}${ageNote}.`);
  }
  if (car.engine) specLines.push(`🐎 المحرك: ${car.engine}${car.horsepower ? ` بقوة ${car.horsepower} حصان` : ''}.`);
  else if (car.horsepower) specLines.push(`🐎 القوة: ${car.horsepower} حصان.`);
  specLines.push(`⚙️ ناقل الحركة: ${trans}.`);
  if (car.color) specLines.push(`🎨 اللون: ${car.color}.`);
  if (car.seats) specLines.push(`👥 المقاعد: ${car.seats}.`);
  specLines.push(isNew ? `✨ الحالة: جديدة تماماً (Zero KM).` : `🛣️ الممشى: ${fmt(car.mileage)} كم.`);

  const facebook = `🔥 ${title} ${newTag} — متوفرة للطلب المباشر من الصين! 🇩🇿🚘

متوفرة الآن للطلب عبر CarDzair مع فحص شامل وتقرير حالة.

💰 تفاصيل السعر:
${priceFb}

✨ أبرز مواصفات السيارة:
${specLines.map((s) => `* ${s}`).join('\n')}

🔗 كل التفاصيل والصور: ${link}

📞 للتواصل والطلب المباشر مع مكاتب CarDzair:
${officesFb}`;

  const instagram = `${title} ${newTag} 🏎️💥
متوفرة الآن للطلب المباشر من الصين لزبائن CarDzair.

💵 السعر:
${priceIg}

🔥 أبرز المواصفات:
${specLines.map((s) => `• ${s}`).join('\n')}

📩 تواصل معنا عبر الخاص (DM) أو اتصل فوراً لحجز سيارتك!
📍 أرقام المكاتب:
${officesDot}

#${tag} #CarDzair #استيراد_السيارات #الجزائر #سيارات_الجزائر #الصين`;

  const tiktok = `${title} ${newTag} 🚘🔥
${priceTt}
${car.engine ? `محرك ${car.engine}${car.horsepower ? ` بقوة ${car.horsepower} حصان` : ''} 🌟` : ''}
للتواصل اطلبنا الآن 📲
${officesDot}

#CarDzair #${tag} #تيك_توك_جزائري #سيارات #استيراد_السيارات #dz`;

  const telegram = `📌 [عرض طلب وتوريد من الصين] ${title} ${newTag}

جاهزة للطلب والشحن المباشر نحو الموانئ الجزائرية عبر CarDzair.

💰 السعر:
${priceTg}

⚙️ المواصفات:
${specLines.map((s) => `• ${s}`).join('\n')}

🔗 التفاصيل الكاملة: ${link}

📞 لطلب السيارة والتواصل مع مكاتب CarDzair:
${officesDot}`;

  // عنوان ووصف مُحسَّنان لبحث يوتيوب وجوجل (الفيديو الأفقي يُفهرس ويجلب زيارات لسنوات)
  const seoTitle = `${title} للبيع في الجزائر — سعر السيارة مستوردة من الصين | CarDzair`.slice(0, 100);
  const seoDescription = `${title} ${newTag} — متوفرة للطلب المباشر من الصين عبر CarDzair.

💰 السعر:
• واصلة للميناء: ${portM} مليون سنتيم ${portNote}${totalM ? `\n• شاملة الجمركة: ${totalM} مليون سنتيم` : ''}

⚙️ المواصفات:
${specLines.map((s) => `• ${s}`).join('\n')}

🔗 كل التفاصيل والصور وحاسبة الجمركة: ${link}

📞 للطلب والتواصل مع مكاتب CarDzair:
${officesDot}

نحن نساعد الجزائريين في استيراد السيارات من الصين: اختيار السيارة، الفحص قبل الشحن، الشحن البحري نحو الموانئ الجزائرية، وحساب الجمركة بدقة.

#${tag} #CarDzair #استيراد_السيارات #سيارات_الجزائر #جمركة_السيارات #سيارات_من_الصين`;

  return { facebook, instagram, tiktok, telegram, seoTitle, seoDescription };
}

// ============ المكوّن الرئيسي ============
const AdminGuaziImport = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('gz_admin_key') || '');
  const [showSettings, setShowSettings] = useState(false);
  const [payload, setPayload] = useState(null);

  const [car, setCar] = useState({
    brand: '', model: '', year: '', mileage: '', transmission: 'automatic', color: '',
    fobUsd: '', customs: '', description: '', engine: '', horsepower: '', seats: '', body: '',
    regDate: '', mfgDate: '',
    priceMode: 'auto', directM: '', directIncludesCustoms: true,
  });
  const [images, setImages] = useState([]); // {src, selected, processedBlob, previewUrl, status}
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [captions, setCaptions] = useState({ facebook: '', instagram: '', tiktok: '', telegram: '' });
  const [captionTab, setCaptionTab] = useState('facebook');
  const [captionsEdited, setCaptionsEdited] = useState(false);
  const [busy, setBusy] = useState('');
  const [fixIdx, setFixIdx] = useState(null); // فهرس الصورة المفتوحة للتصحيح اليدوي
  const [scheduleAt, setScheduleAt] = useState(''); // وقت الجدولة الاختياري
  const [clips, setClips] = useState({ portrait: null, landscape: null }); // فيديو لكل اتجاه
  const [clipTargets, setClipTargets] = useState({ portrait: new Set(['youtube']), landscape: new Set(['youtube']) });
  const [trackChoice, setTrackChoice] = useState('random'); // 'random' أو رابط مقطع محدد
  const [coverPos, setCoverPos] = useState(() => localStorage.getItem('gz_cover_pos') || 'start'); // بطاقة السعر: 'start' أو 'end'
  const [videoOrient, setVideoOrient] = useState('portrait');
  const [autoOrients, setAutoOrients] = useState(['portrait']); // ما يصنعه الزر الواحد
  const [queue, setQueue] = useState(() => { try { return JSON.parse(localStorage.getItem('gz_queue') || '[]'); } catch { return []; } });
  const [queueStart, setQueueStart] = useState('');
  const [queueEvery, setQueueEvery] = useState('24'); // ساعات بين منشور وآخر // portrait 9:16 أو landscape 16:9
  const [fixSpots, setFixSpots] = useState([]); // نسخة عمل للنقاط داخل المحرر
  const [transLogoUrl, setTransLogoUrl] = useState(''); // نسخة شفافة من الشعار لمعاينة المحرر
  const fixWrapRef = useRef(null);
  const [fixWrapW, setFixWrapW] = useState(600); // عرض صورة المحرر بالبكسل — لمطابقة منظور المعاينة مع الرسم النهائي
  const [logoAspect, setLogoAspect] = useState(0.5); // نسبة ارتفاع/عرض اللوجو — لزر "اللوجو = اللافتة"
  const [fixSel, setFixSel] = useState(-1); // النقطة المحددة للتحكم
  const dragRef = useRef(null); // حالة السحب الجارية
  const [log, setLog] = useState([]);
  const [savedCar, setSavedCar] = useState(null);
  const processedOnce = useRef(false);

  const addLog = (msg, ok = true) => setLog((l) => [...l, { msg, ok, t: Date.now() }]);
  const saveQueue = (q) => { setQueue(q); localStorage.setItem('gz_queue', JSON.stringify(q)); };

  const apiBase = (settings.api_base || '').replace(/\/+$/, '');
  const apiHeaders = useMemo(() => ({ 'X-Admin-Key': adminKey }), [adminKey]);

  // ---- تحميل الإعدادات من site_settings ----
  useEffect(() => {
    (async () => {
      const keys = Object.values(SETTING_KEYS);
      const { data } = await supabase.from('site_settings').select('setting_key,setting_value').in('setting_key', keys);
      if (data) {
        const next = { ...DEFAULTS };
        for (const [name, key] of Object.entries(SETTING_KEYS)) {
          const row = data.find((r) => r.setting_key === key);
          if (row && row.setting_value) next[name] = row.setting_value;
        }
        setSettings(next);
        if (!next.api_base) setShowSettings(true);
      }
    })();
  }, []);

  // ---- نسخة شفافة من الشعار لمعاينة المحرر (تُشتق مرة واحدة) ----
  useEffect(() => {
    const url = settings.logo_url || DEFAULTS.logo_url;
    if (!url) return;
    (async () => {
      try {
        const trimmed = await loadTrimmedLogo(url);
        const trans = makeTransparentLogo(trimmed);
        if (trans) {
          setTransLogoUrl(trans.toDataURL('image/png'));
          if (trans.width > 0) setLogoAspect(trans.height / trans.width);
        }
      } catch { /* المعاينة فقط — الرسم النهائي لا يتأثر */ }
    })();
  }, [settings.logo_url]);

  const saveSettings = async () => {
    setBusy('settings');
    for (const [name, key] of Object.entries(SETTING_KEYS)) {
      await supabase.from('site_settings').upsert({ setting_key: key, setting_value: settings[name] || '' }, { onConflict: 'setting_key' });
    }
    localStorage.setItem('gz_admin_key', adminKey);
    setBusy('');
    setShowSettings(false);
    addLog('تم حفظ الإعدادات');
  };

  // ---- قراءة حمولة الـ bookmarklet من الرابط ----
  useEffect(() => {
    const p = parsePayloadFromUrl();
    if (!p) return;
    setPayload(p);
    const title = p.t || '';
    const words = title.split(/\s+/);
    // كتلة المواصفات الخام لها الأولوية؛ الحقول القديمة للتوافق مع زر قديم
    const s = parseSpecBlock(p.spec);
    const engine = s.engine || p.eng || '';
    const hp = s.horsepower || p.hp || '';
    const fuel = s.fuel || p.fuel || '';
    const color = s.color || p.color || '';
    const trRaw = s.transmission || p.tr || '';
    const descParts = [title];
    const specLine = [
      engine ? `المحرك: ${engine}${hp ? ` — ${hp} حصان` : ''}` : '',
      fuel ? `الوقود: ${fuel}` : '',
      color ? `اللون: ${color}` : '',
      s.seats ? `المقاعد: ${s.seats}` : '',
    ].filter(Boolean).join(' | ');
    if (specLine) descParts.push(specLine);
    if (s.regDate) descParts.push(`أول تسجيل: ${s.regDate}${s.mfgDate ? ` | تاريخ الصنع: ${s.mfgDate}` : ''}`);
    descParts.push('مستوردة من الصين عبر CarDzair — فحص شامل وتقرير حالة قبل الشحن.');
    setCar((c) => ({
      ...c,
      brand: words[0] || '',
      model: words.slice(1).join(' ').replace(/20[0-2]\d.*/, '').trim() || title,
      year: s.year || p.y || '',
      mileage: s.mileageKm || p.km || '',
      transmission: /MT/i.test(trRaw) ? 'manual' : 'automatic',
      color,
      fobUsd: p.p || '',
      engine,
      horsepower: hp,
      seats: s.seats || p.seats || '',
      body: s.body || p.body || '',
      regDate: s.regDate || '',
      mfgDate: s.mfgDate || '',
      description: descParts.join('\n'),
    }));
    const spotsStore = loadSpotsStore(); // استرجاع تعديلات سابقة لنفس الصور — تنجو من تحديث الصفحة
    setImages((p.imgs || []).map((src, i) => ({ src, selected: i < 8, processedBlob: null, previewUrl: null, status: 'raw', spots: spotsStore[src] || [] })));
    // تنظيف الرابط حتى لا يعاد الاستيراد عند التحديث
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // ---- معالجة الصور تلقائياً بمجرد وصول بيانات السيارة (ضغطة أقل) ----
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current || !payload || !apiBase || !adminKey) return;
    if (!images.some((im) => im.selected && !im.processedBlob)) return;
    autoRan.current = true;
    processAll(true).then((r) => addLog(`عولجت ${r ? r.length : 0} صور تلقائياً — راجعها ثم اضغط زر التشغيل الكامل`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, apiBase, adminKey, images.length]);

  // ---- الحسابات من Publer ----
  const loadAccounts = useCallback(async () => {
    if (!apiBase || !adminKey) return;
    try {
      const r = await fetch(`${apiBase}/api/publer?action=accounts`, { headers: apiHeaders });
      const data = await r.json();
      if (Array.isArray(data)) {
        setAccounts(data);
        setSelectedAccounts(new Set(data.map((a) => a.id)));
      }
    } catch {
      addLog('تعذر جلب حسابات Publer — تحقق من الإعدادات', false);
    }
  }, [apiBase, adminKey, apiHeaders]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ---- الحساب المالي ----
  const totals = useMemo(() => {
    // وضع السعر المباشر: تكتب الإجمالي بالمليون سنتيم كما هو في الإعلان
    if (car.priceMode === 'direct') {
      const total = Math.round((Number(car.directM) || 0) * 10000);
      return {
        direct: true,
        includesCustoms: car.directIncludesCustoms !== false,
        priceDzd: total, shipping: 0, customs: 0, total,
      };
    }
    const fob = Number(car.fobUsd) || 0;
    const rate = Number(settings.usd_rate) || 0;
    const shipping = Number(settings.shipping) || 0;
    const profit = Number(settings.profit) || 0;
    const customs = Number(car.customs) || 0;
    const priceDzd = Math.round(fob * rate + profit);
    return {
      direct: false,
      includesCustoms: customs > 0,
      priceDzd, shipping, customs, total: priceDzd + shipping + customs,
    };
  }, [car.priceMode, car.directM, car.directIncludesCustoms, car.fobUsd, car.customs, settings]);

  // ---- توليد منشور مخصص لكل منصة (يتوقف التوليد التلقائي إن عدّلت يدوياً) ----
  useEffect(() => {
    if (captionsEdited) return;
    const link = savedCar ? `https://cardzair.com/car/${savedCar.id}` : 'https://cardzair.com';
    setCaptions(buildCaptions({ car, totals, settings, link }));
  }, [car, totals, settings, savedCar, captionsEdited]);

  // ---- معالجة الصور ----
  const proxied = (src) => `${apiBase}/api/img?url=${encodeURIComponent(src)}&k=${encodeURIComponent(adminKey)}`;
  // الصور المرفوعة يدوياً لا تحتاج بروكسي
  const srcOf = (im) => (im.local ? im.src : proxied(im.src));

  const processAll = async (silent = false) => {
    if (!apiBase || !adminKey) { addLog('أدخل رابط الـ API ومفتاح الربط في الإعدادات أولاً', false); setShowSettings(true); return; }
    setBusy('images');
    const next = [...images];
    for (let i = 0; i < next.length; i++) {
      if (!next[i].selected || next[i].status === 'done') continue;
      next[i] = { ...next[i], status: 'working' };
      setImages([...next]);
      try {
        const savedSpots = next[i].spots || [];
        // صورك الخاصة نظيفة أصلاً: لا نبحث فيها عن لافتة Guazi
        const out = await processImage(srcOf(next[i]), settings.banner_text || DEFAULTS.banner_text, settings.logo_url || DEFAULTS.logo_url, savedSpots, savedSpots.length === 0 && !next[i].local);
        next[i] = { ...next[i], processedBlob: out.blob, previewUrl: URL.createObjectURL(out.blob), status: 'done', detected: out.boxes.length, autoBoxes: out.boxes };
      } catch (e) {
        next[i] = { ...next[i], status: 'error' };
        addLog(`فشل معالجة صورة ${i + 1}: ${e.message}`, false);
      }
      setImages([...next]);
    }
    setBusy('');
    processedOnce.current = true;
    if (!silent) addLog('اكتملت معالجة الصور');
    return next.filter((im) => im.selected && im.processedBlob);
  };

  const toggleImage = (i) => setImages((imgs) => imgs.map((im, j) => (j === i ? { ...im, selected: !im.selected } : im)));

  // تصحيح يدوي: إعادة معالجة صورة واحدة بنقاطها
  const reprocessOne = async (i, spots) => {
    setBusy('images');
    try {
      const out = await processImage(srcOf(images[i]), settings.banner_text || DEFAULTS.banner_text, settings.logo_url || DEFAULTS.logo_url, spots, false);
      if (!images[i].local) persistSpots(images[i].src, spots); // تعديلاتك محفوظة — تنجو من تحديث الصفحة
      setImages((imgs) => imgs.map((im, j) => (j === i ? { ...im, spots, processedBlob: out.blob, previewUrl: URL.createObjectURL(out.blob), status: 'done', detected: out.boxes.length, autoBoxes: out.boxes } : im)));
    } catch (e) {
      addLog('فشل التصحيح: ' + e.message, false);
    }
    setBusy('');
  };

  // ---- زر واحد: يعالج ويصنع الفيديو ويحفظ وينشر بالتسلسل ----
  const doEverything = async () => {
    if (!apiBase || !adminKey) { addLog('أدخل رابط الـ API ومفتاح الربط في الإعدادات أولاً', false); setShowSettings(true); return; }
    if (selectedAccounts.size === 0) { addLog('اختر حساباً واحداً على الأقل', false); return; }
    try {
      addLog('▶ بدأ التشغيل الكامل...');
      // 1) معالجة الصور (يتخطّى ما عُولج مسبقاً)
      let ready = images.filter((im) => im.selected && im.processedBlob);
      if (ready.length < images.filter((im) => im.selected).length) {
        ready = await processAll(true);
        addLog(`عولجت ${ready.length} صور`);
      }
      if (!ready.length) { addLog('لا توجد صور جاهزة', false); return; }

      // 2) الفيديوهات المطلوبة
      const nextClips = { ...clips };
      for (const o of autoOrients) {
        if (nextClips[o]) continue;
        const c = await makeVideo(o, ready);
        if (c) nextClips[o] = c;
      }

      // 3) الحفظ في الموقع
      const saved = await saveCar(ready);
      if (!saved) return;

      // 4) النشر أو الجدولة
      await publish({ saved, clips: nextClips, ready });
      addLog('✅ اكتمل كل شيء');
    } catch (e) {
      addLog('توقف التشغيل الكامل: ' + e.message, false);
      setBusy('');
    }
  };

  // ---- الطابور: أضف عدة سيارات ثم وزّع نشرها على أيام ----
  const addToQueue = async () => {
    if (selectedAccounts.size === 0) { addLog('اختر الحسابات أولاً', false); return; }
    try {
      let ready = images.filter((im) => im.selected && im.processedBlob);
      if (!ready.length) ready = await processAll(true);
      if (!ready.length) { addLog('لا توجد صور جاهزة', false); return; }

      const nextClips = { ...clips };
      for (const o of autoOrients) {
        if (nextClips[o]) continue;
        const c = await makeVideo(o, ready);
        if (c) nextClips[o] = c;
      }

      const saved = await saveCar(ready);
      if (!saved) return;

      const link = `https://cardzair.com/car/${saved.id}`;
      // إن لم يكن الرابط النهائي موجوداً بعد، نستبدل الرابط العام برابط السيارة
      const fixLink = (t) => ((t || '').includes('cardzair.com/car/') ? (t || '') : (t || '').split('https://cardzair.com').join(link));
      const item = {
        id: saved.id,
        label: `${car.brand} ${car.model} ${car.year}`.trim(),
        caption: fixLink(captions.facebook),
        captions: Object.fromEntries(Object.entries(captions).map(([k, v]) => [k, fixLink(v)])),
        title: `${car.brand} ${car.model} ${car.year} — CarDzair`.trim(),
        youtubeTitle: captions.seoTitle,
        youtubeDescription: fixLink(captions.seoDescription),
        imageUrls: saved.publicUrls || [saved.image_url, ...(saved.gallery || [])],
        accountIds: [...selectedAccounts],
        videos: [
          nextClips.portrait && { url: nextClips.portrait.videoUrl, providers: [...clipTargets.portrait], isShort: true },
          nextClips.landscape && { url: nextClips.landscape.videoUrl, providers: [...clipTargets.landscape], isShort: false },
        ].filter(Boolean),
      };
      saveQueue([...queue, item]);
      addLog(`أُضيفت "${item.label}" للطابور (${queue.length + 1} في الانتظار) — استورد السيارة التالية`);
    } catch (e) {
      addLog('فشل الإضافة للطابور: ' + e.message, false);
      setBusy('');
    }
  };

  // جدولة كل عناصر الطابور بفواصل زمنية
  const publishQueue = async () => {
    if (!queue.length) return;
    const start = queueStart ? new Date(queueStart) : new Date(Date.now() + 3600e3);
    const gapMs = (Number(queueEvery) || 24) * 3600e3;
    setBusy('queue');
    const left = [];
    for (let i = 0; i < queue.length; i++) {
      const it = queue[i];
      const at = new Date(start.getTime() + i * gapMs);
      try {
        const r = await fetch(`${apiBase}/api/publer`, {
          method: 'POST',
          headers: { ...apiHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...it, scheduledAt: at.toISOString() }),
        });
        const data = await r.json();
        if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
        addLog(`🗓 "${it.label}" → ${at.toLocaleString('ar-DZ')}`);
      } catch (e) {
        addLog(`فشل جدولة "${it.label}": ${e.message}`, false);
        left.push(it);
      }
    }
    saveQueue(left);
    addLog(left.length ? `بقي ${left.length} في الطابور بعد أخطاء` : '✅ جُدول الطابور كاملاً');
    setBusy('');
  };

  const openFixEditor = (i) => {
    const im = images[i];
    const seed = (im.spots && im.spots.length) ? im.spots : (im.autoBoxes || []);
    setFixSpots(seed.map((b) => ({ ...b })));
    setFixSel(seed.length - 1);
    setFixIdx(i);
    // قياس عرض الصورة بعد الرسم — يضبط منظور المعاينة ليطابق الرسم النهائي
    setTimeout(() => { if (fixWrapRef.current) setFixWrapW(fixWrapRef.current.clientWidth || 600); }, 60);
  };

  // ضغطة على الصورة داخل المحرر = إضافة شعار جديد في هذه النقطة
  const handleFixClick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    setFixSpots((s) => [...s, { fx, fy, scale: 0.22, rot: 0, ratio: 0.3, logo: true, ...loadStampDefaults() }]);
    setFixSel(fixSpots.length);
  };

  const startDrag = (e, si) => {
    e.stopPropagation();
    e.preventDefault();
    setFixSel(si);
    const wrap = e.currentTarget.closest('[data-fixwrap]').getBoundingClientRect();
    dragRef.current = { si, wrap };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const fx = Math.min(1, Math.max(0, (ev.clientX - d.wrap.left) / d.wrap.width));
      const fy = Math.min(1, Math.max(0, (ev.clientY - d.wrap.top) / d.wrap.height));
      setFixSpots((s) => s.map((sp, j) => (j === d.si ? { ...sp, fx, fy } : sp)));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const applyFix = async () => {
    const i = fixIdx;
    setFixIdx(null);
    await reprocessOne(i, fixSpots);
    addLog('طُبّق التصحيح على الصورة ✔');
  };

  // رفع صورة واحدة إلى تخزين Supabase وإرجاع رابطها العام
  const uploadBlob = async (blob, prefix = 'cars/gz') => {
    const p = `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}.${blob.type.includes('png') ? 'png' : 'jpg'}`;
    const { error } = await supabase.storage.from('images').upload(p, blob, { contentType: blob.type || 'image/jpeg' });
    if (error) throw error;
    return supabase.storage.from('images').getPublicUrl(p).data.publicUrl;
  };

  // ---- توليد فيديو سلايدشو (9:16) بموسيقى ----
  const makeVideo = async (orientArg, readyArg) => {
    const orient = orientArg || videoOrient;
    const ready = readyArg || images.filter((im) => im.selected && im.processedBlob);
    if (ready.length < 2) { addLog('عالج صورتين على الأقل قبل صنع الفيديو', false); return null; }
    setBusy('video');
    try {
      // 1) بطاقة الغلاف بالعربية
      const isNew = Number(car.mileage) <= 100;
      const portM = Math.round((totals.priceDzd + totals.shipping) / 10000);
      const totalM = totals.customs > 0 ? Math.round(totals.total / 10000) : 0;
      const tracks = parseTracks(settings.music_url);
      const picked = trackChoice === 'random'
        ? (tracks.length ? tracks[Math.floor(Math.random() * tracks.length)] : null)
        : tracks.find((t) => t.url === trackChoice) || null;
      const landscape = orient === 'landscape';
      const cardBlob = await buildTitleCard({
        title: `${car.brand} ${car.model} ${car.year}`.trim(),
        priceLine: totals.direct
          ? `${fmt(Math.round(totals.total / 10000))} مليون سنتيم ${totals.includesCustoms ? 'شاملة الجمركة' : '(بدون جمركة)'}`
          : (totalM ? `${fmt(totalM)} مليون سنتيم شاملة الجمركة` : `${fmt(portM)} مليون سنتيم واصلة للميناء`),
        badges: [
          isNew ? 'جديدة 0 كم' : `${fmt(car.mileage)} كم`,
          car.regDate ? `أول تسجيل ${car.regDate}` : '',
          car.engine ? `${car.engine}${car.horsepower ? ` • ${Math.round(car.horsepower)} حصان` : ''}` : '',
          car.transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي',
        ],
        offices: (settings.offices || '').split('\n').filter(Boolean).map((s) => s.trim()),
        logoUrl: settings.logo_url || DEFAULTS.logo_url,
        landscape,
      });
      addLog('جهّزت بطاقة الغلاف');

      // 2) رفع الغلاف + الصور المعالجة (البطاقة أولاً أو أخيراً حسب الاختيار)
      const coverAtEnd = coverPos === 'end';
      const frames = ready.map((im) => im.processedBlob);
      if (coverAtEnd && frames.length) {
        // شريط تشويق فوق أول صورة حتى يكمل المشاهد للفيديو للآخر
        frames[0] = await addTeaserBanner(frames[0], '💰 السعر والمواصفات في نهاية الفيديو 👇');
        addLog('أضفت شريط التشويق فوق أول صورة');
      }
      const urls = [];
      if (!coverAtEnd) urls.push(await uploadBlob(cardBlob, 'video-src/cover'));
      for (const b of frames) urls.push(await uploadBlob(b, 'video-src/f'));
      if (coverAtEnd) urls.push(await uploadBlob(cardBlob, 'video-src/cover'));
      addLog(`رُفعت ${urls.length} لقطات — جاري التركيب على الخادم...`);

      // 3) التركيب على الخادم
      const r = await fetch(`${apiBase}/api/video`, {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrls: urls,
          musicUrl: picked ? picked.url : '',
          orientation: landscape ? 'landscape' : 'portrait',
          secondsPerImage: Number(settings.video_secs) || 2.5,
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      const clip = { ...data, trackName: picked ? picked.name : '' };
      setClips((c) => ({ ...c, [landscape ? 'landscape' : 'portrait']: clip }));
      addLog(`جاهز! فيديو ${landscape ? 'أفقي' : 'عمودي'} ${data.duration} ثانية (${data.sizeMB} م.ب)${data.hasAudio ? ` 🎵 ${picked.name}` : ' — بلا موسيقى'}`);
      setBusy('');
      return clip;
    } catch (e) {
      addLog('فشل صنع الفيديو: ' + e.message, false);
    }
    setBusy('');
    return null;
  };

  // ---- الحفظ في الموقع ----
  const saveCar = async (readyArg) => {
    const ready = readyArg || images.filter((im) => im.selected && im.processedBlob);
    if (ready.length === 0) { addLog('عالج الصور أولاً قبل الحفظ', false); return null; }
    setBusy('save');
    try {
      const urls = [];
      for (let i = 0; i < ready.length; i++) {
        urls.push(await uploadBlob(ready[i].processedBlob, 'cars/gz'));
      }
      const row = {
        brand: car.brand, model: car.model, year: Number(car.year) || new Date().getFullYear(),
        mileage: Number(car.mileage) || 0,
        price: totals.priceDzd, shipping_price: totals.shipping, customs_price: totals.customs, total_price: totals.total,
        transmission: car.transmission, color: car.color, condition: 'used', status: 'available',
        description: car.description, whatsapp: settings.whatsapp || DEFAULTS.whatsapp,
        image_url: urls[0], gallery: urls.slice(1),
      };
      const { data, error } = await supabase.from('cars_for_sale').insert([row]).select().single();
      if (error) throw error;
      setSavedCar({ ...data, publicUrls: urls });
      addLog(`تم حفظ السيارة في الموقع ✔ (${urls.length} صور)`);
      setBusy('');
      return { ...data, publicUrls: urls };
    } catch (e) {
      addLog('فشل الحفظ: ' + e.message, false);
      setBusy('');
      return null;
    }
  };

  // ---- النشر على السوشيال ----
  const publish = async (opts = {}) => {
    const useClips = opts.clips || clips;
    let saved = opts.saved || savedCar;
    if (!saved) saved = await saveCar(opts.ready);
    if (!saved) return;
    if (selectedAccounts.size === 0) { addLog('اختر حساباً واحداً على الأقل', false); return; }
    setBusy('publish');
    try {
      const fixLink = (t) => (t || '').split('https://cardzair.com/car/').join('https://cardzair.com/car/')
        .replace(/https:\/\/cardzair\.com(?!\/car\/)/g, `https://cardzair.com/car/${saved.id}`);
      const finalCaptions = Object.fromEntries(Object.entries(captions).map(([k, v]) => [k, fixLink(v)]));
      finalCaptions.youtube = fixLink(captions.seoDescription);
      const r = await fetch(`${apiBase}/api/publer`, {
        method: 'POST',
        headers: { ...apiHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caption: finalCaptions.facebook,
          captions: finalCaptions,
          title: `${car.brand} ${car.model} ${car.year} — CarDzair`.trim(),
          imageUrls: saved.publicUrls || [saved.image_url, ...(saved.gallery || [])],
          accountIds: [...selectedAccounts],
          scheduledAt: scheduleAt ? new Date(scheduleAt).toISOString() : undefined,
          videos: [
            useClips.portrait && { url: useClips.portrait.videoUrl, providers: [...clipTargets.portrait], isShort: true },
            useClips.landscape && { url: useClips.landscape.videoUrl, providers: [...clipTargets.landscape], isShort: false },
          ].filter(Boolean),
          youtubeTitle: captions.seoTitle,
          youtubeDescription: captions.seoDescription,
        }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      const what = (data.jobs || []).map((j) => (j.kind === 'video' ? `فيديو → ${j.accounts} حساب` : `صور → ${j.accounts} حساب`)).join(' | ');
      addLog(scheduleAt
        ? `تمت الجدولة ليوم ${new Date(scheduleAt).toLocaleString('ar-DZ')} ✔ (${what})`
        : `تم الإرسال إلى Publer ✔ (${what})`);
      (data.jobs || []).forEach((j) => j.job_id && addLog(`مهمة ${j.kind}: ${j.job_id}`));
    } catch (e) {
      addLog('فشل النشر: ' + e.message, false);
    }
    setBusy('');
  };

  const bookmarklet = useMemo(() => buildBookmarklet(window.location.origin), []);

  const inputCls = 'w-full p-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm';
  const providerLabel = { facebook: 'فايسبوك', instagram: 'انستجرام', tiktok: 'تيك توك', telegram: 'تلجرام', youtube: 'يوتوب' };

  return (
    <div className="space-y-5">
      {/* الرأس */}
      <div className="bg-gradient-to-l from-emerald-600 to-teal-600 rounded-2xl p-5 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2"><SafeIcon icon={LuRocket} className="w-6 h-6" /> استيراد من Guazi + نشر تلقائي</h1>
          <p className="text-emerald-100 text-sm mt-1">اسحب السيارة، عالج الصور، احسب السعر، وانشر على كل الحسابات — بضغطة واحدة.</p>
        </div>
        <button onClick={() => setShowSettings((s) => !s)} className="bg-white/15 hover:bg-white/25 px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2">
          <SafeIcon icon={LuSettings} className="w-4 h-4" /> الإعدادات
        </button>
      </div>

      {/* الإعدادات */}
      {showSettings && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 font-black text-slate-800">إعدادات تُحفظ مرة واحدة</div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">رابط خادم الربط (Vercel API)</label>
            <input dir="ltr" className={inputCls} value={settings.api_base} onChange={(e) => setSettings({ ...settings, api_base: e.target.value })} placeholder="https://cardzair-api.vercel.app" /></div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">مفتاح الربط (Admin Key)</label>
            <input dir="ltr" className={inputCls} value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="يُحفظ في متصفحك فقط" /></div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">سعر صرف الدولار (دج)</label>
            <input type="number" className={inputCls} value={settings.usd_rate} onChange={(e) => setSettings({ ...settings, usd_rate: e.target.value })} /></div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">تكلفة الشحن الافتراضية (دج)</label>
            <input type="number" className={inputCls} value={settings.shipping} onChange={(e) => setSettings({ ...settings, shipping: e.target.value })} /></div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">هامش ربحك — يُدمج في سعر السيارة (دج)</label>
            <input type="number" className={inputCls} value={settings.profit} onChange={(e) => setSettings({ ...settings, profit: e.target.value })} /></div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">رقم الواتساب</label>
            <input dir="ltr" className={inputCls} value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-bold text-slate-600 block mb-1">رابط شعار الموقع (يوضع مكان لوحة Guazi الخضراء)</label>
            <input dir="ltr" className={inputCls} value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-bold text-slate-600 block mb-1">نص احتياطي إن تعذر تحميل الشعار</label>
            <input dir="ltr" className={inputCls} value={settings.banner_text} onChange={(e) => setSettings({ ...settings, banner_text: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-bold text-slate-600 block mb-1">🎵 مكتبة الأصوات — ارفع عدة مقاطع (تملك حقوقها)</label>
            <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
              {parseTracks(settings.music_url).map((t, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-xs font-bold text-slate-700 flex-1 truncate">🎵 {t.name}</span>
                  <audio src={t.url} controls className="h-7 max-w-[160px]" />
                  <button type="button" onClick={() => setSettings((s) => ({
                    ...s,
                    music_url: parseTracks(s.music_url).filter((x) => x.url !== t.url).map((x) => x.name + '|' + x.url).join('\n'),
                  }))} className="text-red-500 hover:text-red-700 bg-red-50 p-1.5 rounded-lg">
                    <SafeIcon icon={LuTrash2} className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {parseTracks(settings.music_url).length === 0 && <p className="text-xs text-slate-400">لا توجد أصوات بعد — الفيديو سيكون بلا موسيقى.</p>}
              <label className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-lg text-xs cursor-pointer">
                {busy === 'music' ? '⏳ جاري الرفع...' : '⬆ أضف مقطعاً صوتياً'}
                <input type="file" accept="audio/*" multiple className="hidden" onChange={async (e) => {
                  const files = [...(e.target.files || [])];
                  if (!files.length) return;
                  setBusy('music');
                  try {
                    const added = [];
                    for (const f of files) {
                      const p = 'music/' + Date.now() + '_' + f.name.replace(/[^\w.-]/g, '_');
                      const { error } = await supabase.storage.from('images').upload(p, f, { contentType: f.type || 'audio/mpeg' });
                      if (error) throw error;
                      const url = supabase.storage.from('images').getPublicUrl(p).data.publicUrl;
                      added.push(f.name.replace(/\.[^.]+$/, '') + '|' + url);
                    }
                    setSettings((s) => ({ ...s, music_url: [s.music_url, ...added].filter(Boolean).join('\n') }));
                    addLog('أُضيف ' + added.length + ' مقطع صوتي — اضغط حفظ الإعدادات');
                  } catch (err) { addLog('فشل رفع الصوت: ' + err.message, false); }
                  setBusy('');
                  e.target.value = '';
                }} />
              </label>
            </div>
          </div>
          <div><label className="text-xs font-bold text-slate-600 block mb-1">⏱ ثوانٍ لكل صورة في الفيديو</label>
            <input type="number" step="0.5" min="1.5" max="6" className={inputCls} value={settings.video_secs} onChange={(e) => setSettings({ ...settings, video_secs: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-bold text-slate-600 block mb-1">أرقام المكاتب (سطر لكل مكتب: الاسم: الرقم)</label>
            <textarea rows={3} className={inputCls} value={settings.offices} onChange={(e) => setSettings({ ...settings, offices: e.target.value })} /></div>
          <button onClick={saveSettings} disabled={busy === 'settings'} className="sm:col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl font-bold flex items-center justify-center gap-2">
            <SafeIcon icon={LuSave} className="w-4 h-4" /> حفظ الإعدادات
          </button>
        </div>
      )}

      {/* الـ bookmarklet */}
      {!payload && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 font-black text-blue-800 mb-2"><SafeIcon icon={LuBookmark} className="w-5 h-5" /> زر السحب السحري — يُثبَّت مرة واحدة</div>
          <ol className="text-sm text-blue-900/80 space-y-1 mb-3 pr-4 list-decimal">
            <li>اسحب الزر الأخضر أدناه إلى شريط المفضلة في متصفحك (اظهره بـ Ctrl+Shift+B).</li>
            <li>افتح أي سيارة على <a href="https://en.guazi.com/used-cars/" target="_blank" rel="noreferrer" className="underline font-bold">en.guazi.com</a>.</li>
            <li>اضغط الزر من المفضلة — ستُفتح هذه الصفحة والبيانات معبأة تلقائياً.</li>
          </ol>
          <label className="float-left inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-black px-5 py-3 rounded-xl cursor-pointer shadow">
            📷 أو ارفع صورك من جهازك
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
              const files = [...(e.target.files || [])];
              if (!files.length) return;
              setPayload({ manual: true, t: '', url: '' });
              setImages(files.map((f, i) => ({
                src: URL.createObjectURL(f), local: true, file: f,
                selected: i < 10, processedBlob: null, previewUrl: null, status: 'raw', spots: [],
              })));
              addLog(`أُضيفت ${files.length} صور من جهازك — املأ البيانات ثم اضغط التشغيل الكامل`);
              e.target.value = '';
            }} />
          </label>
          <a ref={(el) => { if (el) el.setAttribute('href', bookmarklet); }} onClick={(e) => e.preventDefault()} draggable className="inline-flex items-center gap-2 bg-emerald-600 text-white font-black px-5 py-3 rounded-xl cursor-grab shadow">
            <SafeIcon icon={LuSparkles} className="w-4 h-4" /> ⬅ كار دزاير: اسحب السيارة
          </a>
          <button onClick={() => { navigator.clipboard.writeText(bookmarklet); addLog('نُسخ كود الزر — أنشئ مفضلة جديدة والصقه في خانة الرابط'); }} className="ms-3 text-xs font-bold text-blue-700 underline inline-flex items-center gap-1">
            <SafeIcon icon={LuCopy} className="w-3 h-3" /> أو انسخ الكود يدوياً
          </button>
        </div>
      )}

      {/* البيانات المستوردة */}
      {payload && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-black text-slate-800">1️⃣ بيانات السيارة</h2>
              {payload.url ? <a href={payload.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-bold inline-flex items-center gap-1"><SafeIcon icon={LuExternalLink} className="w-3 h-3" /> المصدر</a> : <span className="text-xs font-bold text-slate-400">📷 صور من جهازك</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-bold text-slate-600 block mb-1">الماركة</label><input className={inputCls} value={car.brand} onChange={(e) => setCar({ ...car, brand: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">الموديل</label><input className={inputCls} value={car.model} onChange={(e) => setCar({ ...car, model: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">السنة</label><input type="number" className={inputCls} value={car.year} onChange={(e) => setCar({ ...car, year: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">الممشى (كم)</label><input type="number" className={inputCls} value={car.mileage} onChange={(e) => setCar({ ...car, mileage: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">ناقل الحركة</label>
                <select className={inputCls + ' bg-white'} value={car.transmission} onChange={(e) => setCar({ ...car, transmission: e.target.value })}>
                  <option value="automatic">أوتوماتيك</option><option value="manual">يدوي</option>
                </select></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">اللون</label><input className={inputCls} value={car.color} onChange={(e) => setCar({ ...car, color: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">المحرك (مثال: 1.5T)</label><input dir="ltr" className={inputCls} value={car.engine} onChange={(e) => setCar({ ...car, engine: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">القوة (حصان)</label><input type="number" className={inputCls} value={car.horsepower} onChange={(e) => setCar({ ...car, horsepower: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">المقاعد</label><input type="number" className={inputCls} value={car.seats} onChange={(e) => setCar({ ...car, seats: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-slate-600 block mb-1">أول تسجيل (شهر/سنة)</label><input dir="ltr" placeholder="01/2026" className={inputCls} value={car.regDate} onChange={(e) => setCar({ ...car, regDate: e.target.value })} /></div>
            </div>
            <div className="mt-3"><label className="text-xs font-bold text-slate-600 block mb-1">الوصف</label>
              <textarea rows={3} className={inputCls} value={car.description} onChange={(e) => setCar({ ...car, description: e.target.value })} /></div>

            {/* التسعير */}
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex gap-2 mb-3">
                {[['auto', '🧮 احسب من سعر الصين'], ['direct', '✍️ أكتب السعر النهائي']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setCar({ ...car, priceMode: k })}
                    className={'px-3 py-2 rounded-xl text-xs font-black border-2 transition-all ' + (car.priceMode === k ? 'border-emerald-500 bg-white text-emerald-700' : 'border-slate-200 text-slate-400')}>
                    {label}
                  </button>
                ))}
              </div>

              {car.priceMode === 'direct' ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">السعر النهائي (مليون سنتيم)</label>
                    <input type="number" step="0.5" className={inputCls} value={car.directM} placeholder="مثال: 525"
                      onChange={(e) => setCar({ ...car, directM: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">هل يشمل الجمركة؟</label>
                    <div className="flex gap-2">
                      {[[true, 'شامل ✅'], [false, 'بدون ⚠️']].map(([v, lab]) => (
                        <button key={String(v)} type="button" onClick={() => setCar({ ...car, directIncludesCustoms: v })}
                          className={'flex-1 px-2 py-2.5 rounded-xl text-xs font-bold border-2 ' + (car.directIncludesCustoms === v ? 'border-emerald-500 bg-white text-emerald-700' : 'border-slate-200 text-slate-400')}>
                          {lab}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="bg-emerald-600 text-white rounded-xl p-3 text-center">
                    <div className="text-[10px] font-bold opacity-80">يظهر للزبون</div>
                    <div className="text-lg font-black">{fmt(Math.round(totals.total / 10000))} مليون سنتيم</div>
                    <div className="text-[10px] font-bold opacity-80">{fmt(totals.total)} دج</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                  <div><label className="text-xs font-bold text-slate-600 block mb-1">FOB بالدولار ($)</label>
                    <input type="number" className={inputCls} value={car.fobUsd} onChange={(e) => setCar({ ...car, fobUsd: e.target.value })} /></div>
                  <div className="text-center text-xs text-slate-500 font-bold">× {settings.usd_rate} دج<br />+ ربح {fmt(settings.profit)}<br />= <span className="text-emerald-700 text-sm font-black">{fmt(totals.priceDzd)} دج</span></div>
                  <div><label className="text-xs font-bold text-slate-600 block mb-1">الشحن (دج)</label>
                    <input type="number" className={inputCls} value={settings.shipping} onChange={(e) => setSettings({ ...settings, shipping: e.target.value })} /></div>
                  <div><label className="text-xs font-bold text-slate-600 block mb-1">الجمركة (دج)</label>
                    <input type="number" className={inputCls} value={car.customs} onChange={(e) => setCar({ ...car, customs: e.target.value })} placeholder="من الحاسبة" /></div>
                  <div className="bg-emerald-600 text-white rounded-xl p-3 text-center">
                    <div className="text-[10px] font-bold opacity-80">الإجمالي النهائي</div>
                    <div className="text-lg font-black">{fmt(totals.total)} دج</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* الصور */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-black text-slate-800 flex items-center gap-2"><SafeIcon icon={LuImage} className="w-5 h-5 text-indigo-600" /> 2️⃣ الصور ({images.filter((i) => i.selected).length} مختارة)</h2>
              <button onClick={() => processAll()} disabled={busy === 'images'} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-60">
                <SafeIcon icon={busy === 'images' ? LuLoader : LuSparkles} className={`w-4 h-4 ${busy === 'images' ? 'animate-spin' : ''}`} />
                عالج الصور (شريط + شعار)
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {images.map((im, i) => (
                <div key={i} onClick={() => toggleImage(i)} className={`relative aspect-square rounded-xl overflow-hidden border-4 cursor-pointer transition-all ${im.selected ? 'border-emerald-500' : 'border-transparent opacity-40'}`}>
                  <img src={im.previewUrl || (im.local || (apiBase && adminKey) ? srcOf(im) : im.src)} alt="" className={'w-full h-full ' + (im.previewUrl ? 'object-contain bg-slate-100' : 'object-cover')} loading="lazy" />
                  {im.selected && <span className="absolute top-1.5 right-1.5 bg-emerald-500 text-white rounded-full p-1"><SafeIcon icon={LuCheck} className="w-3 h-3" /></span>}
                  {im.selected && im.status === 'done' && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); openFixEditor(i); }} title="صحّح موضع الشعار يدوياً" className="absolute top-1.5 left-1.5 bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black px-1.5 py-1 rounded-lg shadow">🎯 صحّح</button>
                  )}
                  {im.status === 'done' && (
                    <span className={'absolute bottom-1.5 left-1.5 text-white text-[9px] font-black px-1.5 py-0.5 rounded ' + (im.detected ? 'bg-indigo-600' : 'bg-slate-500')}>
                      {im.detected ? 'شعار ×' + im.detected + ' ✓' : 'بلا لافتة'}
                    </span>
                  )}
                  {im.status === 'working' && <span className="absolute inset-0 bg-black/40 flex items-center justify-center"><SafeIcon icon={LuLoader} className="w-6 h-6 text-white animate-spin" /></span>}
                  {im.status === 'error' && <span className="absolute bottom-1.5 left-1.5 bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded">خطأ</span>}
                </div>
              ))}
            </div>
          </div>

          {/* الفيديو */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-black text-slate-800 flex items-center gap-2"><SafeIcon icon={LuVideo} className="w-5 h-5 text-red-600" /> 🎬 فيديو للشورتس والريلز (اختياري)</h2>
              <button onClick={() => makeVideo()} disabled={!!busy} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-60">
                <SafeIcon icon={busy === 'video' ? LuLoader : LuVideo} className={`w-4 h-4 ${busy === 'video' ? 'animate-spin' : ''}`} />
                {busy === 'video' ? 'جاري التركيب...' : (clips[videoOrient] ? 'أعد صنع ' : 'اصنع ') + (videoOrient === 'portrait' ? 'العمودي 📱' : 'الأفقي 🖥')}
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">بطاقة غلاف عربية + صور السيارة. تيك توك يضيف موسيقى بنفسه للصور، فالفيديو أساساً ليوتيوب شورتس.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">📐 الاتجاه</label>
                <div className="flex gap-2">
                  {[['portrait', '📱 عمودي 9:16', 'شورتس وريلز وتيك توك'], ['landscape', '🖥 أفقي 16:9', 'يوتيوب عادي وفايسبوك']].map(([k, label, hint]) => (
                    <button key={k} type="button" onClick={() => setVideoOrient(k)} title={hint}
                      className={'flex-1 px-3 py-2 rounded-xl text-xs font-black border-2 transition-all ' + (videoOrient === k ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-400')}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1.5">🎵 الصوت ({parseTracks(settings.music_url).length} في المكتبة)</label>
                <select value={trackChoice} onChange={(e) => setTrackChoice(e.target.value)} className={inputCls + ' bg-white'}>
                  <option value="random">🎲 عشوائي (يختار لي)</option>
                  {parseTracks(settings.music_url).map((t) => <option key={t.url} value={t.url}>{t.name}</option>)}
                </select>
                {trackChoice !== 'random' && (
                  <audio key={trackChoice} controls preload="none" src={trackChoice} className="w-full mt-1.5 h-9" />
                )}
                {trackChoice === 'random' && parseTracks(settings.music_url).length > 0 && (
                  <p className="text-[11px] text-slate-400 mt-1.5">💡 اختر مقطعاً من القائمة ليظهر مشغّل تسمعه به قبل صنع الفيديو.</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-600 block mb-1.5">🪧 مكان بطاقة السعر والمواصفات</label>
                <div className="flex gap-2">
                  {[
                    ['start', '🏁 في البداية', 'الفيديو يبدأ ببطاقة السعر مباشرة'],
                    ['end', '🎬 في النهاية + تشويق', 'صور السيارة أولاً وشريط "السعر في نهاية الفيديو" فوق أول صورة — المشاهد يكمل للآخر'],
                  ].map(([k, label, hint]) => (
                    <button key={k} type="button" title={hint}
                      onClick={() => { setCoverPos(k); localStorage.setItem('gz_cover_pos', k); }}
                      className={'flex-1 px-3 py-2 rounded-xl text-xs font-black border-2 transition-all ' + (coverPos === k ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-400')}>
                      {label}
                    </button>
                  ))}
                </div>
                {coverPos === 'end' && (
                  <p className="text-[11px] text-amber-600 font-bold mt-1.5">سيُضاف تلقائياً شريط «💰 السعر والمواصفات في نهاية الفيديو 👇» فوق أول صورة.</p>
                )}
              </div>
            </div>

            {['portrait', 'landscape'].filter((k) => clips[k]).map((k) => {
              const cl = clips[k];
              const isP = k === 'portrait';
              const opts = isP
                ? [['youtube', 'يوتيوب شورتس'], ['instagram', 'انستجرام ريلز'], ['facebook', 'فايسبوك ريلز'], ['tiktok', 'تيك توك (بدل الصور)']]
                : [['youtube', 'يوتيوب (فيديو عادي — سيو)'], ['facebook', 'فايسبوك فيديو']];
              return (
                <div key={k} className="flex flex-col sm:flex-row gap-4 items-start bg-slate-50 rounded-xl p-3 border border-slate-200 mb-3">
                  <video src={cl.videoUrl} controls className={'rounded-xl bg-black w-full ' + (isP ? 'sm:w-40' : 'sm:w-64')} />
                  <div className="flex-1 space-y-2">
                    <p className="text-xs font-black text-slate-800">{isP ? '📱 عمودي 9:16' : '🖥 أفقي 16:9'} <span className="font-bold text-slate-500">• ⏱ {cl.duration}ث • 📦 {cl.sizeMB} م.ب • {cl.hasAudio ? '🎵 ' + (cl.trackName || 'بموسيقى') : '🔇 بلا صوت'}</span></p>
                    <p className="text-xs font-bold text-slate-700">أرسله إلى:</p>
                    <div className="flex flex-wrap gap-2">
                      {opts.map(([p, label]) => {
                        const on = clipTargets[k].has(p);
                        return (
                          <button key={p} onClick={() => setClipTargets((t) => {
                            const nx = new Set(t[k]);
                            if (nx.has(p)) nx.delete(p); else nx.add(p);
                            return { ...t, [k]: nx };
                          })} className={'px-3 py-1.5 rounded-lg text-xs font-bold border-2 ' + (on ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-400')}>{label}{on ? ' ✓' : ''}</button>
                        );
                      })}
                    </div>
                    <button onClick={() => setClips((c) => ({ ...c, [k]: null }))} className="text-[11px] text-red-500 font-bold underline">حذف هذا الفيديو</button>
                  </div>
                </div>
              );
            })}
            {(clips.portrait || clips.landscape) && (
              <p className="text-[11px] text-slate-400">الحسابات غير المحددة تستقبل منشور الصور المعتاد. يمكنك صنع الاتجاهين معاً: يوتيوب يستقبل شورت + فيديو عادي للسيو.</p>
            )}
          </div>

          {/* النشر */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-black text-slate-800 flex items-center gap-2 mb-4"><SafeIcon icon={LuShare2} className="w-5 h-5 text-pink-600" /> 3️⃣ النشر — منشور مخصص لكل منصة</h2>
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {[['facebook', 'فايسبوك'], ['instagram', 'انستجرام'], ['tiktok', 'تيك توك'], ['telegram', 'تلجرام']].map(([k, label]) => (
                <button key={k} onClick={() => setCaptionTab(k)} className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 transition-all ${captionTab === k ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 text-slate-500'}`}>{label}</button>
              ))}
              {captionsEdited && (
                <button onClick={() => setCaptionsEdited(false)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 underline flex items-center gap-1">
                  <SafeIcon icon={LuRefreshCw} className="w-3 h-3" /> إعادة التوليد التلقائي
                </button>
              )}
            </div>
            <textarea rows={12} className={inputCls} value={captions[captionTab]} onChange={(e) => { setCaptionsEdited(true); setCaptions({ ...captions, [captionTab]: e.target.value }); }} />
            <div className="flex flex-wrap gap-2 mt-4">
              {accounts.length === 0 && <p className="text-xs text-slate-400">لم تُجلب حسابات Publer بعد — تأكد من الإعدادات ثم <button onClick={loadAccounts} className="underline font-bold">أعد المحاولة</button></p>}
              {accounts.map((a) => {
                const on = selectedAccounts.has(a.id);
                return (
                  <button key={a.id} onClick={() => setSelectedAccounts((s) => { const n = new Set(s); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-bold transition-all ${on ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-400'}`}>
                    {a.picture && <img src={a.picture} alt="" className="w-5 h-5 rounded-full" />}
                    {providerLabel[a.provider] || a.provider} — {a.name}
                    {on && <SafeIcon icon={LuCheck} className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
            {/* الطابور: عمل أسبوع في جلسة واحدة */}
            <div className="mt-4 bg-violet-50 border-2 border-violet-200 rounded-2xl p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="font-black text-violet-800 text-sm">📚 الطابور — أضف عدة سيارات ثم وزّعها على الأيام</p>
                <button onClick={addToQueue} disabled={!!busy} className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-xl font-bold text-xs disabled:opacity-60">
                  ＋ أضف هذه السيارة للطابور
                </button>
              </div>
              {queue.length === 0 ? (
                <p className="text-xs text-violet-600/70">الطابور فارغ. أضف السيارة الحالية، ثم استورد التالية وأضفها — وفي الأخير وزّع النشر على الأيام بضغطة.</p>
              ) : (
                <>
                  <div className="space-y-1.5 mb-3">
                    {queue.map((it, i) => (
                      <div key={it.id} className="flex items-center gap-2 bg-white border border-violet-200 rounded-lg px-3 py-2">
                        <span className="text-xs font-black text-violet-700">{i + 1}.</span>
                        <span className="text-xs font-bold text-slate-700 flex-1 truncate">{it.label}</span>
                        <span className="text-[10px] font-bold text-slate-400">{it.imageUrls.length} صور{it.videos.length ? ` • ${it.videos.length} فيديو` : ''}</span>
                        <button onClick={() => saveQueue(queue.filter((x) => x.id !== it.id))} className="text-red-500 bg-red-50 p-1 rounded">
                          <SafeIcon icon={LuTrash2} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-violet-700 block mb-1">أول منشور</label>
                      <input type="datetime-local" value={queueStart} onChange={(e) => setQueueStart(e.target.value)} className="p-2 border border-violet-200 rounded-lg text-xs bg-white outline-none" />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-violet-700 block mb-1">كل كم ساعة</label>
                      <input type="number" min="1" value={queueEvery} onChange={(e) => setQueueEvery(e.target.value)} className="w-20 p-2 border border-violet-200 rounded-lg text-xs bg-white outline-none" />
                    </div>
                    <button onClick={publishQueue} disabled={!!busy} className="bg-violet-700 hover:bg-violet-800 text-white px-5 py-2.5 rounded-xl font-black text-xs disabled:opacity-60">
                      🗓 جدوِل الطابور ({queue.length})
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* الزر الواحد: يعالج ويصنع الفيديو ويحفظ وينشر */}
            <div className="mt-5 bg-gradient-to-l from-emerald-600 to-teal-600 rounded-2xl p-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-white font-black text-sm">🚀 التشغيل الكامل بضغطة واحدة — يصنع أيضاً:</span>
                {[['portrait', '📱 عمودي'], ['landscape', '🖥 أفقي']].map(([k, label]) => {
                  const on = autoOrients.includes(k);
                  return (
                    <button key={k} type="button" onClick={() => setAutoOrients((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]))}
                      className={'px-3 py-1.5 rounded-lg text-xs font-black border-2 ' + (on ? 'border-white bg-white/20 text-white' : 'border-white/30 text-white/60')}>
                      {label}{on ? ' ✓' : ''}
                    </button>
                  );
                })}
                {autoOrients.length === 0 && <span className="text-white/70 text-xs font-bold">بلا فيديو — صور فقط</span>}
              </div>
              <button onClick={doEverything} disabled={!!busy}
                className="w-full bg-white hover:bg-slate-50 text-emerald-700 py-3.5 rounded-xl font-black text-base flex items-center justify-center gap-2 disabled:opacity-60">
                <SafeIcon icon={busy ? LuLoader : LuRocket} className={'w-5 h-5 ' + (busy ? 'animate-spin' : '')} />
                {busy ? 'جاري العمل...' : scheduleAt ? 'افعل كل شيء وجدوِل 🗓' : 'افعل كل شيء وانشر الآن 🚀'}
              </button>
            </div>

            {/* جدولة اختيارية: اتركها فارغة للنشر الفوري */}
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
              <label className="text-sm font-black text-violet-800">🗓 جدولة النشر (اختياري):</label>
              <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="p-2 border border-violet-200 rounded-lg text-sm outline-none focus:border-violet-500 bg-white" />
              {scheduleAt && (
                <>
                  <span className="text-xs font-bold text-violet-600">سيُنشر: {new Date(scheduleAt).toLocaleString('ar-DZ')}</span>
                  <button onClick={() => setScheduleAt('')} className="text-xs text-red-500 font-bold underline">إلغاء الجدولة</button>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <button onClick={saveCar} disabled={!!busy} className="bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 disabled:opacity-60">
                <SafeIcon icon={busy === 'save' ? LuLoader : LuSave} className={`w-5 h-5 ${busy === 'save' ? 'animate-spin' : ''}`} /> حفظ في الموقع فقط
              </button>
              <button onClick={publish} disabled={!!busy} className={`text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 disabled:opacity-60 hover:opacity-90 ${scheduleAt ? 'bg-gradient-to-l from-violet-600 to-purple-600' : 'bg-gradient-to-l from-pink-600 to-rose-600'}`}>
                <SafeIcon icon={busy === 'publish' ? LuLoader : LuRocket} className={`w-5 h-5 ${busy === 'publish' ? 'animate-spin' : ''}`} />
                {scheduleAt ? 'حفظ + جدولة النشر 🗓' : 'حفظ + نشر على السوشيال 🚀'}
              </button>
            </div>
            {savedCar && (
              <a href={`/car/${savedCar.id}`} target="_blank" rel="noreferrer" className="block text-center mt-3 text-emerald-700 font-bold text-sm underline">عرض السيارة في الموقع ←</a>
            )}
          </div>
        </>
      )}

      {/* محرر التصحيح اليدوي: إضافة/سحب/تكبير/تدوير الشعار */}
      {fixIdx !== null && images[fixIdx] && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4" onClick={() => setFixIdx(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-4 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="font-black text-slate-800 mb-1">🎯 محرر الشعارات</p>
            <p className="text-xs text-slate-500 mb-2">ما تراه هنا هو ما سيُرسم بالضبط. اسحب الشعار لضبط مكانه، وبالمنزلقات غيّر الحجم والدوران، واضغط على أي مكان فارغ لإضافة شعار جديد.</p>

            <div data-fixwrap ref={fixWrapRef} className="relative rounded-xl overflow-hidden cursor-crosshair select-none" onClick={handleFixClick}>
              <img src={srcOf(images[fixIdx])} alt="" className="w-full h-auto block pointer-events-none" draggable={false} />
              {fixSpots.map((sp, si) => (
                <div key={si} onPointerDown={(e) => startDrag(e, si)} onClick={(e) => e.stopPropagation()}
                  className={`absolute cursor-move flex items-center justify-center ${sp.logo === false ? 'bg-slate-900 shadow-lg' : ''} ${fixSel === si ? 'ring-4 ring-amber-400' : 'ring-1 ring-slate-300/60'}`}
                  style={{
                    left: `${sp.fx * 100}%`, top: `${sp.fy * 100}%`,
                    width: `${(sp.scale || 0.22) * 100}%`, aspectRatio: `1 / ${sp.ratio || 0.3}`,
                    transform: `translate(-50%, -50%) rotate(${sp.rot || 0}deg) perspective(${Math.max(120, Math.round((sp.scale || 0.22) * fixWrapW * Math.max(1, sp.ratio || 0.3) * 2.2))}px) rotateY(${sp.ry || 0}deg) rotateX(${-(sp.rx || 0)}deg)`,
                    borderRadius: sp.style === 'trans' ? '6px' : `${Math.round((sp.corner !== undefined ? sp.corner : 44) * 0.32)}% / ${(sp.corner !== undefined ? sp.corner : 44)}%`,
                    ...(sp.logo !== false && sp.style !== 'trans' ? {
                      background: 'linear-gradient(#ffffff, #e6e7ea)',
                      backgroundImage: 'radial-gradient(circle at 9% 18%, #9aa0a8 0 4%, transparent 5%), radial-gradient(circle at 91% 18%, #9aa0a8 0 4%, transparent 5%), radial-gradient(circle at 9% 82%, #9aa0a8 0 4%, transparent 5%), radial-gradient(circle at 91% 82%, #9aa0a8 0 4%, transparent 5%), linear-gradient(#ffffff, #e6e7ea)',
                      border: (sp.border !== undefined ? sp.border : 50) > 0
                        ? `${Math.max(1, Math.round(((sp.border !== undefined ? sp.border : 50) / 50) * 2))}px solid rgba(120,120,128,0.6)`
                        : 'none',
                      boxShadow: (sp.shadow !== undefined ? sp.shadow : 50) > 0
                        ? `0 ${Math.round(((sp.shadow !== undefined ? sp.shadow : 50) / 50) * 4)}px ${Math.round(((sp.shadow !== undefined ? sp.shadow : 50) / 50) * 10)}px rgba(0,0,0,${(0.45 * Math.min(1.6, (sp.shadow !== undefined ? sp.shadow : 50) / 50)).toFixed(2)})`
                        : 'none',
                    } : {}),
                    ...(sp.logo !== false && sp.style === 'trans' ? {
                      filter: (sp.shadow !== undefined ? sp.shadow : 50) > 0
                        ? `drop-shadow(0 ${Math.round(((sp.shadow !== undefined ? sp.shadow : 50) / 50) * 3)}px ${Math.round(((sp.shadow !== undefined ? sp.shadow : 50) / 50) * 4)}px rgba(0,0,0,0.5))`
                        : 'none',
                    } : {}),
                  }}>
                  {sp.logo === false
                    ? <span className="text-white text-[9px] font-black pointer-events-none">تغطية</span>
                    : <img src={transLogoUrl || settings.logo_url || DEFAULTS.logo_url} alt="" className={`object-contain pointer-events-none ${sp.style === 'trans' ? 'drop-shadow-md' : ''}`}
                        style={sp.style === 'trans'
                          ? { maxWidth: '100%', maxHeight: '100%' }
                          : { maxWidth: `${100 - 2 * (sp.margin !== undefined ? sp.margin : 14)}%`, maxHeight: `${100 - 2 * (sp.margin !== undefined ? sp.margin : 14)}%` }}
                        draggable={false} />}
                </div>
              ))}
            </div>

            {fixSel >= 0 && fixSpots[fixSel] && (() => {
              const sp = fixSpots[fixSel];
              const upd = (patch) => setFixSpots((s) => s.map((x, j) => (j === fixSel ? { ...x, ...patch } : x)));
              // منزلقات مبنية مباشرة (بدون مكوّن متداخل) حتى لا يُعاد إنشاؤها أثناء السحب
              // العرض والطول مستقلان فعلياً: تغيير العرض يعوّض النسبة حتى لا يتغير الطول
              const scaleNow = sp.scale || 0.22;
              const ratioNow = sp.ratio || 0.3;
              const heightFrac = scaleNow * ratioNow; // الطول كنسبة من عرض الصورة
              const clampRatio = (r) => Math.min(3, Math.max(0.05, r));
              const sliders = [
                { label: '↔️ العرض', unit: '%', min: 6, max: 70, val: Math.round(scaleNow * 100), set: (v) => upd({ scale: v / 100, ratio: clampRatio(heightFrac / (v / 100)) }) },
                { label: '↕️ الطول', unit: '%', min: 2, max: 45, val: Math.round(heightFrac * 100), set: (v) => upd({ ratio: clampRatio((v / 100) / scaleNow) }) },
                { label: '◽ استدارة الحواف', unit: '%', min: 0, max: 100, val: sp.corner !== undefined ? sp.corner : 44, set: (v) => upd({ corner: v }) },
                { label: '🔍 حجم اللوجو', unit: '%', min: 40, max: 100, val: 100 - 2 * (sp.margin !== undefined ? sp.margin : 14), set: (v) => upd({ margin: (100 - v) / 2 }) },
                { label: '⭕ خط الإطار', unit: '%', min: 0, max: 100, val: sp.border !== undefined ? sp.border : 50, set: (v) => upd({ border: v }) },
                { label: '🌑 الظل', unit: '%', min: 0, max: 100, val: sp.shadow !== undefined ? sp.shadow : 50, set: (v) => upd({ shadow: v }) },
                { label: '🔄 الدوران', unit: '°', min: -90, max: 90, val: sp.rot || 0, set: (v) => upd({ rot: v }) },
                { label: '↔️ منظور أفقي', unit: '°', min: -70, max: 70, val: sp.ry || 0, set: (v) => upd({ ry: v }) },
                { label: '↕ منظور عمودي', unit: '°', min: -70, max: 70, val: sp.rx || 0, set: (v) => upd({ rx: v }) },
              ];
              const styleBtns = [
                { key: 'plate', label: '⬜ لافتة بيضاء', active: sp.logo !== false && (sp.style || 'plate') === 'plate', on: () => upd({ logo: true, style: 'plate' }) },
                { key: 'trans', label: '✨ شعار شفاف', active: sp.logo !== false && sp.style === 'trans', on: () => upd({ logo: true, style: 'trans' }) },
                { key: 'cover', label: '⬛ تغطية', active: sp.logo === false, on: () => upd({ logo: false, style: 'plate' }) },
              ];
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="col-span-2 sm:col-span-3 flex gap-2">
                    {styleBtns.map((b) => (
                      <button key={b.key} type="button" onClick={b.on}
                        className={`flex-1 text-xs font-black py-2 rounded-lg border-2 transition ${b.active ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                  {sliders.map((sl) => (
                    <div key={sl.label}>
                      <label className="text-xs font-bold text-slate-600 flex justify-between"><span>{sl.label}</span><span>{sl.val}{sl.unit}</span></label>
                      <input type="range" min={sl.min} max={sl.max} value={sl.val}
                        onChange={(e) => sl.set(Number(e.target.value))} className="w-full accent-amber-500" />
                    </div>
                  ))}
                  <div className="flex items-end gap-2">
                    <button type="button" onClick={() => upd({ rot: 0, ry: 0, rx: 0 })} className="flex-1 text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2 rounded-lg">↺ تصفير الميلان</button>
                    <button type="button" title="يجعل اللافتة بنفس نسبة اللوجو تماماً ويملؤها به — بلا حواف بيضاء زائدة"
                      onClick={() => upd({ ratio: Math.min(3, Math.max(0.05, logoAspect)), margin: 0 })}
                      className="flex-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold py-2 rounded-lg">📐 اللوجو = اللافتة</button>
                    <button type="button" title="يحفظ شكل هذه اللافتة (النمط، الاستدارة، حجم اللوجو، خط الإطار، الظل) ليُطبق تلقائياً على كل اللافتات المكتشفة والجديدة"
                      onClick={() => { saveStampDefaults(sp); addLog('حُفظ هذا الشكل كافتراضي — سيُطبق على كل اللافتات القادمة والاكتشاف التلقائي ✔'); }}
                      className="flex-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold py-2 rounded-lg">💾 اجعله الافتراضي</button>
                  </div>
                </div>
              );
            })()}
            <div className="flex flex-wrap gap-2 mt-3 justify-end">
              {fixSel >= 0 && fixSpots.length > 0 && (
                <button onClick={() => { setFixSpots((s) => s.filter((_, j) => j !== fixSel)); setFixSel(-1); }} className="text-xs bg-red-100 text-red-700 font-bold px-3 py-2 rounded-lg">🗑 حذف المحدد</button>
              )}
              {fixSpots.length > 0 && (
                <button onClick={() => { setFixSpots([]); setFixSel(-1); }} className="text-xs bg-red-50 text-red-500 font-bold px-3 py-2 rounded-lg">مسح الكل</button>
              )}
              <button onClick={() => setFixIdx(null)} className="text-xs bg-slate-100 text-slate-600 font-bold px-3 py-2 rounded-lg">إلغاء</button>
              <button onClick={applyFix} disabled={!!busy} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-black px-5 py-2 rounded-lg">✅ تطبيق على الصورة</button>
            </div>
          </div>
        </div>
      )}

      {/* السجل */}
      {log.length > 0 && (
        <div className="bg-slate-900 rounded-2xl p-4 space-y-1.5">
          {log.slice(-8).map((l, i) => (
            <div key={i} className={`text-xs font-bold flex items-center gap-2 ${l.ok ? 'text-emerald-400' : 'text-red-400'}`}>
              <SafeIcon icon={l.ok ? LuCheck : LuCircleAlert} className="w-3.5 h-3.5 flex-shrink-0" /> {l.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGuaziImport;
